/**
 * Product Default Card JavaScript
 *
 * Follows the exact pattern from product-card.js for consistency and reliability.
 * Handles gallery navigation, swatch interactions, and variant updates.
 *
 * Uses declarative event system (on: attributes) for event binding.
 */

import { ThemeEvents, VariantUpdateEvent, VariantSelectedEvent, CartAddEvent, CartErrorEvent } from '@theme/events';
import { debounce } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { morph } from '@theme/morph';

/**
 * Main Product Default Card Component
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {HTMLAnchorElement} [cardGalleryLink] - The gallery link element.
 * @property {HTMLAnchorElement} [productTitleLink] - The title link element.
 *
 * @extends {Component<Refs>}
 */
class ProductDefaultCard extends Component {
  requiredRefs = ['productCardLink'];

  /** @type {boolean} - Track if navigation arrows were used (disables card hover) */
  #navigationUsed = false;

  /** @type {boolean} - Track if a swatch was selected/clicked (disables card hover) */
  #swatchSelected = false;

  /** @type {string | null} - Currently hovered swatch media ID */
  #hoveredSwatchMediaId = null;

  /** @type {string | null} - Currently selected variant ID */
  #selectedVariantId = null;

  /** @type {boolean} - Track if gallery navigation has been initialized to prevent duplicates */
  #galleryNavigationInitialized = false;

  /** @type {boolean} - Track if card hover listeners have been bound to prevent duplicates */
  #cardHoverInitialized = false;

  /** @type {MutationObserver | null} - Watches the slides wrapper so nav state follows the real slide count */
  #slidesObserver = null;

  /** @type {Element | null} - Slides wrapper the observer is currently attached to */
  #observedSlidesWrapper = null;

  /** @type {number} - Slide count the navigation state was last calculated from */
  #lastSyncedSlideCount = -1;

  /** @type {Map<HTMLElement, Function>} - Store navigation button click handlers for cleanup */
  #navigationHandlers = new Map();

  /** @type {Map<HTMLElement, Function>} - Store dot click handlers for cleanup */
  #dotHandlers = new Map();

  /** @type {{ start: EventListener, move: EventListener, end: EventListener } | null} - Store touch swipe handlers for cleanup */
  #touchHandlers = null;

  /** @type {boolean} - Track if add to cart request is in progress to prevent multiple API calls */
  #addToCartInProgress = false;

  /** @type {number | null} - Timeout to reset add to cart in progress flag if API call hangs */
  #addToCartTimeout = null;

  /** @type {Map<HTMLButtonElement, { checkmarkTimeout: number, resetTimeout: number, restoreTimeout: number | null }>} - Store active animations to cancel on error */
  #activeAnimations = new Map();

  /** @type {number | null} - Timeout for auto-hiding error message */
  #errorMessageTimeout = null;

  /** @type {HTMLElement | null} - Slide that was --active before card hover (to restore on leave) */
  #hoverPreviousActiveSlide = null;

  /** @type {HTMLElement | null} - Slide we marked as --active during card hover */
  #hoverNextActiveSlide = null;

  /** @type {HTMLElement | null} - Dot that was --active before card hover (to restore on leave) */
  #hoverPreviousActiveDot = null;

  /** @type {HTMLElement | null} - Dot we marked as --active during card hover */
  #hoverNextActiveDot = null;

  /**
   * Tracks the currently-running horizontal slide animation (arrows/dots/
   * swipe). Used so a follow-up navigation can finalise the previous
   * transition before starting a new one.
   *
   * @type {{
   *   currentSlide: HTMLElement,
   *   nextSlide: HTMLElement,
   *   onComplete: (() => void) | null,
   *   handleTransitionEnd: ((event: TransitionEvent) => void) | null,
   *   timeoutId: number | null,
   *   finalised: boolean,
   * } | null}
   */
  #activeSlideAnimation = null;

  /** Duration of a horizontal slide transition in milliseconds. Must match CSS. */
  static #SLIDE_ANIMATION_DURATION_MS = 450;

  get productPageUrl() {
    return this.refs.productCardLink?.href || '';
  }

  connectedCallback() {
    super.connectedCallback();

    // Initialize selected variant from default visible slide
    this.#initializeSelectedVariant();

    // Initialize gallery navigation
    this.#initializeGalleryNavigation();

    // Slides can arrive after this point: snippets/perf-defer-script.liquid parks
    // every non-active slide in a <template> until the first interaction, and this
    // module is imported lazily (snippets/lazy-module-script.liquid), so the card
    // often initializes while it only has one slide. Watch for the real set.
    this.#observeSlideChanges();

    // Initialize card hover behavior
    this.#initializeCardHover();

    // Listen for variant updates from the swatch picker
    // Use capture phase to stop events before they reach section-level listeners
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdateCapture, true);
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelectedCapture, true);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    
    // Listen for cart add events to open cart drawer
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    
    // Listen for cart errors to show error messages
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError);

    // When pointer leaves the card, restore any hover-preview state (safety net if resetVariant wasn’t called)
    this.addEventListener('pointerleave', this.#handleCardPointerLeave);

    // Setup add to cart checkmark animation
    this.#setupAddToCartCheckmarkAnimation();
    
    // Setup form submission debounce to prevent multiple API calls
    this.#setupFormSubmissionDebounce();

    // Watch for the interacted class on <html> to load deferred images
    this.#watchInteractedClass();
  }

  /**
   * Initialize selected variant from default visible slide
   * Handles out of stock products where all slides might be hidden initially
   */
  #initializeSelectedVariant() {
    const slides = this.slides;
    if (slides.length === 0) return;

    // Strategy: Find the best slide to show
    // Priority: 1) Visible + active, 2) Visible, 3) Active (even if hidden), 4) First slide
    let defaultSlide = null;

    // Priority 1: Find slide that's both visible AND has active class
    defaultSlide = slides.find(slide => 
      !slide.hidden && slide.classList.contains('card-gallery-default__slide--active')
    );

    // Priority 2: Find any visible slide (regardless of class)
    if (!defaultSlide) {
      defaultSlide = slides.find(slide => !slide.hidden);
    }

    // Priority 3: Find first slide with active class (even if hidden) - handles out of stock products
    if (!defaultSlide) {
      defaultSlide = slides.find(slide => 
        slide.classList.contains('card-gallery-default__slide--active')
      );
    }

    // Priority 4: Use the first slide as fallback - ensures at least one image shows
    if (!defaultSlide) {
      defaultSlide = slides[0];
    }

    // Ensure the default slide is visible
    if (defaultSlide) {
      // Clear all slides first - remove all state classes and hide them
      slides.forEach(slide => {
        slide.classList.remove(
          'card-gallery-default__slide--active',
          'card-gallery-default__slide--swatch-selected',
          'card-gallery-default__slide--swatch-hovered',
          'card-gallery-default__slide--card-hover'
        );
        slide.hidden = true;
      });

      // Show the default slide
      defaultSlide.hidden = false;
      defaultSlide.classList.add('card-gallery-default__slide--active');

      // Set selected variant ID
      if (defaultSlide.dataset.variantId) {
        this.#selectedVariantId = defaultSlide.dataset.variantId;
      }

      // Update dots and arrows visibility (run when dots exist, even without variantId for no-swatches case)
      const variantId = defaultSlide.dataset.variantId || null;
      const navItems = this.querySelectorAll('[data-gallery-nav-item]');
      if (navItems.length > 0) {
        this.#updateDotsForVariant(variantId);
      }
      this.#updateNavArrowsVisibility();
    }
  }

  /**
   * Public method to re-initialize the gallery
   * Called after filter updates to ensure all cards are properly initialized
   * Removes old event listeners before adding new ones to prevent duplicates
   */
  reinitializeGallery() {
    if (this.gallery && this.slides.length > 0) {
      // CRITICAL: Remove old event listeners before re-initializing to prevent duplicates
      this.#removeGalleryNavigationListeners();
      
      this.#initializeSelectedVariant();
      this.#initializeGalleryNavigation();
      this.#observeSlideChanges();
    }
  }

  /**
   * Remove gallery navigation event listeners to prevent duplicates
   * Called before re-initialization after filter/pagination updates
   */
  #removeGalleryNavigationListeners() {
    const gallery = this.gallery;
    if (!gallery) return;

    // Remove navigation arrow handlers
    this.#navigationHandlers.forEach((handler, element) => {
      element.removeEventListener('click', /** @type {EventListener} */ (handler));
    });
    this.#navigationHandlers.clear();

    // Remove dot click handlers
    this.#dotHandlers.forEach((handler, element) => {
      element.removeEventListener('click', /** @type {EventListener} */ (handler));
    });
    this.#dotHandlers.clear();

    // Remove touch swipe handlers
    if (this.#touchHandlers) {
      const mediaWrapper = gallery.querySelector('.card-gallery-default__media-wrapper');
      if (mediaWrapper) {
        mediaWrapper.removeEventListener('touchstart', /** @type {EventListener} */ (this.#touchHandlers.start));
        mediaWrapper.removeEventListener('touchmove', /** @type {EventListener} */ (this.#touchHandlers.move));
        mediaWrapper.removeEventListener('touchend', /** @type {EventListener} */ (this.#touchHandlers.end));
      }
      this.#touchHandlers = null;
    }

    // Reset initialization flag
    this.#galleryNavigationInitialized = false;
  }

  /**
   * Watch the slides wrapper for slides being added or removed.
   *
   * Parked slides are already counted up front, so this is the safety net
   * rather than the primary mechanism: it covers `morph` on variant change, the
   * deferral script parking slides after this component has initialized, and
   * any other late change to the slide set.
   */
  #observeSlideChanges() {
    const wrapper = this.querySelector('.card-gallery-default__slides-wrapper');
    if (!wrapper || wrapper === this.#observedSlidesWrapper) return;

    this.#slidesObserver?.disconnect();
    this.#observedSlidesWrapper = wrapper;
    this.#lastSyncedSlideCount = this.slides.length;

    this.#slidesObserver = new MutationObserver(() => {
      const count = this.slides.length;
      if (count === this.#lastSyncedSlideCount) return;
      this.#lastSyncedSlideCount = count;
      this.#refreshGalleryNavState();
    });

    this.#slidesObserver.observe(wrapper, { childList: true });
  }

  /**
   * Recalculate dots, progress bar and arrow visibility from the slides that
   * are currently in the document, keeping the slide that is on screen active.
   */
  #refreshGalleryNavState() {
    const slides = this.slides;
    if (slides.length === 0) return;

    const activeSlide =
      slides.find((slide) => !slide.hidden && slide.classList.contains('card-gallery-default__slide--active')) ??
      slides.find((slide) => !slide.hidden);

    const activeIndex = activeSlide
      ? parseInt(activeSlide.dataset.imageIndex || activeSlide.dataset.slideIndex || '0', 10)
      : 0;

    this.#updateDotsForVariant(this.#getSelectedVariantId(), activeIndex);
    this.#updateNavArrowsVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdateCapture, true);
    this.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.removeEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartError, this.#handleCartError);
    this.removeEventListener('pointerleave', this.#handleCardPointerLeave);
    this.removeEventListener('mouseenter', this.#handleCardHoverEnter);
    this.removeEventListener('mouseleave', this.#handleCardHoverLeave);
    this.#cardHoverInitialized = false;

    this.#slidesObserver?.disconnect();
    this.#slidesObserver = null;
    this.#observedSlidesWrapper = null;
    this.#lastSyncedSlideCount = -1;

    // Clean up gallery navigation event listeners
    this.#removeGalleryNavigationListeners();

    // Finalise any in-flight slide animation so its safety-net timer doesn't
    // try to mutate detached DOM nodes after the card is removed.
    this.#cancelSlideAnimation();
  }

  /** Safety net: restore hover state when pointer leaves the card (in case swatch pointerleave didn’t fire) */
  #handleCardPointerLeave = () => {
    this.#restoreHoverPreviewState('pointerleave-card');
    this.#clearSwatchHoverState();
    this.#handleCardHoverLeave();
  };

  /**
   * Initialize gallery navigation (arrows and dots)
   * Tracks event handlers for cleanup to prevent duplicate listeners
   */
  #initializeGalleryNavigation() {
    const gallery = this.gallery;
    if (!gallery) return;

    // Prevent duplicate initialization
    if (this.#galleryNavigationInitialized) {
      return;
    }

    // Bind arrow navigation with handler tracking
    const prevButton = gallery.querySelector('.card-gallery-default__arrow--prev');
    const nextButton = gallery.querySelector('.card-gallery-default__arrow--next');

    if (prevButton instanceof HTMLElement) {
      /** @type {EventListener} */
      const prevHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.#navigateGallery('prev');
      };
      prevButton.addEventListener('click', prevHandler);
      this.#navigationHandlers.set(prevButton, prevHandler);
    }

    if (nextButton instanceof HTMLElement) {
      /** @type {EventListener} */
      const nextHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.#navigateGallery('next');
      };
      nextButton.addEventListener('click', nextHandler);
      this.#navigationHandlers.set(nextButton, nextHandler);
    }

    // Bind dot navigation (variant-specific) with handler tracking
    // Uses array index instead of data-image-index to prevent skipping slides
    const navItems = gallery.querySelectorAll('[data-gallery-nav-item]');
    navItems.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      /** @type {EventListener} */
      const navHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.#navigationUsed = true;
        this.#clearSwatchHoverState();

        const variantId = this.#getSelectedVariantId();
        if (!variantId) return;

        const variantSlides = this.#getSlidesForVariant(variantId);
        const slideIndex = parseInt(item.dataset.slideIndex || '0', 10);
        const targetSlide = variantSlides[slideIndex];

        if (targetSlide) {
          this.#showSlide(targetSlide);
        }
      };
      item.addEventListener('click', navHandler);
      this.#dotHandlers.set(item, navHandler);
    });

    // Initialize touch swipe for mobile
    this.#initializeTouchSwipe(gallery);

    // Mark as initialized
    this.#galleryNavigationInitialized = true;
  }

  /**
   * Initialize touch swipe navigation for mobile
   * Stores handlers for cleanup to prevent duplicate listeners
   * @param {HTMLElement} gallery
   */
  #initializeTouchSwipe(gallery) {
    let touchStartX = 0;
    let touchStartY = 0;
    let deltaX = 0;
    let deltaY = 0;
    const minSwipeDistance = 50;

    /** @type {EventListener} */
    const handleTouchStart = (e) => {
      const touchEvent = /** @type {TouchEvent} */ (e);
      if (touchEvent.touches.length !== 1) return;
      const touch = touchEvent.touches[0];
      if (!touch) return;
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
    };

    /** @type {EventListener} */
    const handleTouchMove = (e) => {
      const touchEvent = /** @type {TouchEvent} */ (e);
      if (touchEvent.touches.length !== 1) return;
      const touch = touchEvent.touches[0];
      if (!touch) return;
      deltaX = touch.clientX - touchStartX;
      deltaY = touch.clientY - touchStartY;
      // Prevent scroll for horizontal swipes
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        e.preventDefault();
      }
    };

    /** @type {EventListener} */
    const handleTouchEnd = () => {
      // Check if valid horizontal swipe
      if (Math.abs(deltaX) < minSwipeDistance || Math.abs(deltaX) <= Math.abs(deltaY)) return;

      const variantId = this.#getSelectedVariantId();
      if (!variantId) return;

      const variantSlides = this.#getSlidesForVariant(variantId);
      if (variantSlides.length <= 1) return;

      this.#navigationUsed = true;
      this.#clearSwatchHoverState();
      this.#navigateGallery(deltaX > 0 ? 'prev' : 'next');
    };

    const mediaWrapper = gallery.querySelector('.card-gallery-default__media-wrapper');
    if (mediaWrapper) {
      mediaWrapper.addEventListener('touchstart', handleTouchStart, { passive: true });
      mediaWrapper.addEventListener('touchmove', handleTouchMove, { passive: false });
      mediaWrapper.addEventListener('touchend', handleTouchEnd, { passive: true });

      // Store handlers for cleanup
      this.#touchHandlers = {
        start: handleTouchStart,
        move: handleTouchMove,
        end: handleTouchEnd
      };
    }
  }

  /**
   * Initialize card hover behavior for showing secondary image
   */
  #initializeCardHover() {
    // Only enable card hover on desktop (1200px+) - disable on tablet/mobile (up to 1199px)
    if (window.innerWidth < 1200) {
      return; // Don't initialize card hover on tablet/mobile
    }

    if (this.#cardHoverInitialized) {
      return;
    }
    
    // Add mouseenter and mouseleave listeners to product card
    this.addEventListener('mouseenter', this.#handleCardHoverEnter);
    this.addEventListener('mouseleave', this.#handleCardHoverLeave);
    this.#cardHoverInitialized = true;
  }

  /**
   * Load deferred images on a slide by swapping data-src → src.
   * Only loads images that haven't been loaded yet.
   * @param {HTMLElement} slide
   */
  #loadSlideImages(slide) {
    if (!slide || slide.dataset.imagesLoaded === 'true') return;
    const imgs = slide.querySelectorAll('img[data-src]');
    imgs.forEach((img) => {
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    });
    slide.dataset.imagesLoaded = 'true';
  }

  /**
   * Load all remaining deferred images across every slide.
   * Called once when <html> gains the .interacted class.
   */
  #loadAllDeferredImages() {
    this.slides.forEach((slide) => this.#loadSlideImages(slide));
  }

  /**
   * Watch for the .interacted class on <html> and load all deferred
   * images once the user has interacted with the page.
   */
  #watchInteractedClass() {
    if (document.documentElement.classList.contains('interacted')) {
      this.#loadAllDeferredImages();
      return;
    }
    const observer = new MutationObserver(() => {
      if (document.documentElement.classList.contains('interacted')) {
        this.#loadAllDeferredImages();
        observer.disconnect();
      }
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  }

  /**
   * Handle card hover enter - show secondary image of current/selected variant.
   *
   * On desktop (>=1200px), the second slide for the current variant becomes the
   * active slide (`card-gallery-default__slide--active`) and its matching dot
   * receives `card-gallery-default__dot--active`. Original active states are
   * stored so they can be restored on hover-leave. The existing
   * `card-gallery-default__slide--card-hover` class is still applied so the
   * established hide/show CSS continues to work unchanged.
   */
  #handleCardHoverEnter = () => {
    // Disable card hover on tablet/mobile (up to 1199px) - same as mobile view
    if (window.innerWidth < 1200) {
      return;
    }

    // Don't show hover if swatch is being hovered (swatch hover takes priority)
    if (this.#hoveredSwatchMediaId) {
      return;
    }

    // Rapid re-enter (or duplicate listeners) must not advance to the next/last
    // slide. Restore is based on the slide that was active when hover started.
    if (this.#hoverNextActiveSlide instanceof HTMLElement) {
      return;
    }

    // Allow card hover even after navigation - clear navigation flag on hover
    if (this.#navigationUsed) {
      this.#navigationUsed = false;
    }

    const variantId = this.#getSelectedVariantId();
    if (!variantId) return;

    const variantSlides = this.#getSlidesForVariant(variantId);
    if (variantSlides.length < 2) return; // Need at least 2 images to show secondary

    // Find the currently visible/active slide for this variant.
    const currentSlide = variantSlides.find((slide) => !slide.hidden && (
      slide.classList.contains('card-gallery-default__slide--active') ||
      slide.classList.contains('card-gallery-default__slide--swatch-selected')
    )) || variantSlides.find((slide) => !slide.hidden) || variantSlides[0];

    if (!currentSlide) return;

    const currentSlideIndex = variantSlides.indexOf(currentSlide);
    // Show the following image, but never wrap to slide 0. Wrapping would leave
    // `--active` on the last slide after mouseleave.
    const nextSlideIndex = currentSlideIndex + 1;
    if (nextSlideIndex >= variantSlides.length) return;
    const nextSlide = variantSlides[nextSlideIndex];

    if (!nextSlide || nextSlide === currentSlide) return;

    // Remember the pre-hover slide once so leave can restore it exactly.
    this.#hoverPreviousActiveSlide = currentSlide;
    this.#hoverNextActiveSlide = nextSlide;

    // Keep the existing hide/show hover effect by applying --card-hover and
    // ensuring the next slide is not hidden.
    nextSlide.hidden = false;
    this.#loadSlideImages(nextSlide);
    nextSlide.classList.add('card-gallery-default__slide--card-hover');

    // Swap the --active class from current slide to the next (second) slide.
    currentSlide.classList.remove('card-gallery-default__slide--active');
    nextSlide.classList.add('card-gallery-default__slide--active');

    // Hide the previously-active (first child) slide so only the second slide
    // renders. CSS uses `:not([hidden]):first-child` as a visibility fallback,
    // so we explicitly set the `hidden` attribute here.
    currentSlide.hidden = true;

    // Mirror the swap on the navigation dots so the second dot shows as active.
    this.#syncHoverDotActiveState(nextSlideIndex, variantSlides.length);
  };

  /**
   * Handle card hover leave - restore the original --active classes on the
   * slide and dot, and clean up the --card-hover marker.
   */
  #handleCardHoverLeave = () => {
    const nextSlide = this.#hoverNextActiveSlide;
    let previousSlide = this.#hoverPreviousActiveSlide;

    if (!(previousSlide instanceof HTMLElement) && !(nextSlide instanceof HTMLElement)) {
      this.slides.forEach((slide) => {
        slide.classList.remove('card-gallery-default__slide--card-hover');
      });
      return;
    }

    if (!(previousSlide instanceof HTMLElement)) {
      const variantId = this.#getSelectedVariantId();
      const variantSlides = variantId ? this.#getSlidesForVariant(variantId) : this.slides;
      previousSlide = variantSlides[0] || this.slides[0] || null;
    }

    // Restore exactly one visible --active slide (the one from before hover).
    // Hide every other slide so the last/hover image cannot stay on top of the
    // first via leftover `--active` or the `:first-child` CSS fallback.
    this.slides.forEach((slide) => {
      slide.classList.remove(
        'card-gallery-default__slide--card-hover',
        'card-gallery-default__slide--active'
      );
      if (slide !== previousSlide) {
        slide.hidden = true;
      }
    });

    if (previousSlide instanceof HTMLElement) {
      previousSlide.classList.add('card-gallery-default__slide--active');
      previousSlide.hidden = false;
      this.#loadSlideImages(previousSlide);
    }

    // Restore dot --active state.
    if (this.#hoverNextActiveDot instanceof HTMLElement) {
      this.#hoverNextActiveDot.classList.remove('card-gallery-default__dot--active');
      this.#hoverNextActiveDot.setAttribute('aria-selected', 'false');
    }
    if (this.#hoverPreviousActiveDot instanceof HTMLElement) {
      this.#hoverPreviousActiveDot.classList.add('card-gallery-default__dot--active');
      this.#hoverPreviousActiveDot.setAttribute('aria-selected', 'true');
    }

    // Clear tracking refs.
    this.#hoverPreviousActiveSlide = null;
    this.#hoverNextActiveSlide = null;
    this.#hoverPreviousActiveDot = null;
    this.#hoverNextActiveDot = null;

    if (previousSlide instanceof HTMLElement) {
      const restoredIndex = parseInt(previousSlide.dataset.imageIndex || previousSlide.dataset.slideIndex || '0', 10);
      this.#syncGalleryNavItems(restoredIndex);
    }
  };

  /**
   * Smoothly slide between two slides horizontally for navigation actions
   * (arrows, dots, touch swipe).
   *
   * Both slides are temporarily promoted to `position: absolute` via the
   * `card-gallery-default__slide--animating` class so they can translate
   * across the gallery without affecting the parent's layout. The incoming
   * slide starts off-screen on the side dictated by `direction` and animates
   * to the centre while the outgoing slide animates to the opposite side.
   *
   * If a previous animation is still in flight it is finalised immediately
   * (its `onComplete` is invoked and inline styles cleared) so state stays
   * consistent before the new transition starts.
   *
   * If reduced motion is requested or the two slides are the same node, the
   * function short-circuits and invokes `onComplete` synchronously.
   *
   * NOTE: This animation is intentionally only used for the navigation
   * actions wired into `#showSlide`. Card hover keeps the existing instant
   * hide/show swap (no slide animation).
   *
   * @param {HTMLElement} currentSlide - The slide that is currently visible.
   * @param {HTMLElement} nextSlide - The slide that should become visible.
   * @param {'next' | 'prev'} direction - Which side the incoming slide enters from. `next` enters from the right, `prev` from the left.
   * @param {() => void} [onComplete] - Called once the animation finishes (or is finalised).
   */
  #animateSlideTransition(currentSlide, nextSlide, direction, onComplete) {
    if (
      !(currentSlide instanceof HTMLElement) ||
      !(nextSlide instanceof HTMLElement) ||
      currentSlide === nextSlide
    ) {
      if (typeof onComplete === 'function') onComplete();
      return;
    }

    this.#cancelSlideAnimation();

    const prefersReducedMotion =
      typeof globalThis.matchMedia === 'function' &&
      globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      if (typeof onComplete === 'function') onComplete();
      return;
    }

    const enterFromX = direction === 'next' ? '100%' : '-100%';
    const exitToX = direction === 'next' ? '-100%' : '100%';
    const ANIMATING_CLASS = 'card-gallery-default__slide--animating';

    // Outgoing slide: pin at centre, ready to translate away.
    currentSlide.classList.add(ANIMATING_CLASS);
    currentSlide.style.transition = 'none';
    currentSlide.style.transform = 'translateX(0)';

    // Incoming slide: make visible, pin off-screen, no transition yet.
    nextSlide.hidden = false;
    nextSlide.classList.add(ANIMATING_CLASS);
    nextSlide.style.transition = 'none';
    nextSlide.style.transform = `translateX(${enterFromX})`;

    // Force a reflow so the initial transforms are committed before the
    // transition kicks in on the next frame.
    nextSlide.getBoundingClientRect();

    const animation = {
      currentSlide,
      nextSlide,
      /** @type {(() => void) | null} */
      onComplete: typeof onComplete === 'function' ? onComplete : null,
      /** @type {((event: TransitionEvent) => void) | null} */
      handleTransitionEnd: null,
      /** @type {number | null} */
      timeoutId: null,
      finalised: false,
    };
    this.#activeSlideAnimation = animation;

    const finalise = () => {
      if (animation.finalised) return;
      animation.finalised = true;

      if (animation.timeoutId !== null) {
        clearTimeout(animation.timeoutId);
        animation.timeoutId = null;
      }
      if (animation.handleTransitionEnd) {
        nextSlide.removeEventListener('transitionend', animation.handleTransitionEnd);
      }

      [currentSlide, nextSlide].forEach((slide) => {
        slide.classList.remove(ANIMATING_CLASS);
        slide.style.transform = '';
        slide.style.transition = '';
      });

      if (this.#activeSlideAnimation === animation) {
        this.#activeSlideAnimation = null;
      }

      if (typeof animation.onComplete === 'function') {
        animation.onComplete();
      }
    };

    animation.handleTransitionEnd = (event) => {
      if (event.target !== nextSlide) return;
      if (event.propertyName !== 'transform') return;
      finalise();
    };
    nextSlide.addEventListener('transitionend', animation.handleTransitionEnd);

    // Safety net: ensure the animation always finalises even if `transitionend`
    // doesn't fire (e.g. element removed from the DOM, transition cancelled).
    animation.timeoutId = setTimeout(
      finalise,
      ProductDefaultCard.#SLIDE_ANIMATION_DURATION_MS + 100,
    );

    // Trigger the transition on the next frame.
    requestAnimationFrame(() => {
      if (animation.finalised) return;
      currentSlide.style.transition = '';
      nextSlide.style.transition = '';
      currentSlide.style.transform = `translateX(${exitToX})`;
      nextSlide.style.transform = 'translateX(0)';
    });
  }

  /**
   * Finalise any in-flight slide animation immediately. The previous
   * animation's `onComplete` callback (if any) is invoked so that follow-up
   * navigation starts from a consistent state.
   */
  #cancelSlideAnimation() {
    const animation = this.#activeSlideAnimation;
    if (!animation || animation.finalised) return;

    animation.finalised = true;
    if (animation.timeoutId !== null) {
      clearTimeout(animation.timeoutId);
      animation.timeoutId = null;
    }
    if (animation.handleTransitionEnd) {
      animation.nextSlide.removeEventListener('transitionend', animation.handleTransitionEnd);
    }

    [animation.currentSlide, animation.nextSlide].forEach((slide) => {
      if (!(slide instanceof HTMLElement)) return;
      slide.classList.remove('card-gallery-default__slide--animating');
      slide.style.transform = '';
      slide.style.transition = '';
    });

    this.#activeSlideAnimation = null;

    if (typeof animation.onComplete === 'function') {
      animation.onComplete();
    }
  }

  /**
   * Reset the `--active` state on the navigation dots and activate only the
   * dot matching the slide that becomes active on card hover.
   *
   * Always wipes `--active` from every visible dot first (and updates
   * `aria-selected` accordingly) so that any stale multi-active state is
   * normalised by a single hover. The originally-active dot (if any) is
   * remembered so `#handleCardHoverLeave` can restore it when the user moves
   * the mouse off the card without navigating.
   *
   * @param {number} nextDotIndex - Target dot index (matches the second slide).
   * @param {number} variantSlideCount - Number of slides for the current variant.
   */
  #syncHoverDotActiveState(nextDotIndex, variantSlideCount) {
    const dots = Array.from(this.querySelectorAll('.card-gallery-default__dot'))
      .filter((dot) => dot instanceof HTMLElement && !dot.hidden);

    if (dots.length < 2 || variantSlideCount < 2) return;

    // Resolve the target dot for the second slide.
    const targetDotIndex = nextDotIndex < dots.length ? nextDotIndex : dots.length - 1;
    const nextDot = dots[targetDotIndex];
    if (!(nextDot instanceof HTMLElement)) return;

    // Remember the first currently-active dot (if any, and not the target) so
    // we can restore it on hover-leave.
    const currentlyActiveDot = dots.find((dot) => dot.classList.contains('card-gallery-default__dot--active'));
    this.#hoverPreviousActiveDot =
      currentlyActiveDot instanceof HTMLElement && currentlyActiveDot !== nextDot
        ? currentlyActiveDot
        : null;

    // Reset --active on every visible dot so we end with exactly one active dot.
    dots.forEach((dot) => {
      if (dot === nextDot) return;
      if (dot.classList.contains('card-gallery-default__dot--active')) {
        dot.classList.remove('card-gallery-default__dot--active');
        dot.setAttribute('aria-selected', 'false');
      }
    });

    nextDot.classList.add('card-gallery-default__dot--active');
    nextDot.setAttribute('aria-selected', 'true');
    this.#hoverNextActiveDot = nextDot;
    this.#syncGalleryNavItems(targetDotIndex);
  }

  /**
   * Navigate gallery (prev/next) - only within selected variant's images
   * Uses array indices instead of data-image-index to prevent skipping slides
   * @param {string} direction
   */
  #navigateGallery(direction) {
    // Get selected variant ID
    const variantId = this.#getSelectedVariantId();
    if (!variantId) return;

    // Get slides for this variant only
    const variantSlides = this.#getSlidesForVariant(variantId);
    if (variantSlides.length === 0) return;

    // Mark navigation as used - this disables card hover logic
    this.#navigationUsed = true;
    this.#clearSwatchHoverState();

    // Find current slide's index within variant slides array
    const currentSlideIndex = variantSlides.findIndex(slide => !slide.hidden);
    if (currentSlideIndex === -1) return;

    // Calculate new index based on array position (not data-image-index)
    let newSlideIndex;

    if (direction === 'next') {
      newSlideIndex = (currentSlideIndex + 1) % variantSlides.length;
    } else {
      newSlideIndex = (currentSlideIndex - 1 + variantSlides.length) % variantSlides.length;
    }

    // Get slide directly from array by index
    const nextSlide = variantSlides[newSlideIndex];

    if (nextSlide) {
      this.#showSlide(nextSlide);
    }
  }

  /**
   * Get current slide index
   * @returns {number}
   */
  #getCurrentSlideIndex() {
    const slides = this.slides;
    return slides.findIndex((slide) => !slide.hidden);
  }

  /**
   * Show slide by index
   * @param {number} index
   */
  #showSlideByIndex(index) {
    this.#restoreHoverPreviewState('showSlideByIndex');
    const slides = this.slides;
    if (index < 0 || index >= slides.length) return;

    const slideToShow = slides[index];
    if (slideToShow) {
      this.#clearSwatchHoverState();
      this.#showSlide(slideToShow);
    }
  }

  /**
   * Show slide by media ID
   * @param {string} mediaId
   * @param {boolean} [isSelected=false] - Whether this is from a selected swatch (not hover)
   */
  #showSlideByMediaId(mediaId, isSelected = false) {
    if (!mediaId) {
      // If no media ID provided, show first slide as fallback
      this.#showSlideByIndex(0);
      return;
    }

    const slideToShow = this.slides.find((slide) => String(slide.dataset.mediaId) === String(mediaId));

    if (slideToShow) {
      // Clear all slides first (but don't hide if they're the first slide and no selection was made)
      this.slides.forEach((slide) => {
        slide.classList.remove(
          'card-gallery-default__slide--active',
          'card-gallery-default__slide--swatch-selected',
          'card-gallery-default__slide--swatch-hovered',
          'card-gallery-default__slide--card-hover'
        );
        // Only hide if this is a selection or navigation action
        if (isSelected || this.#navigationUsed) {
          slide.hidden = true;
        }
      });

      // Show the target slide with appropriate state class
      slideToShow.hidden = false;
      this.#loadSlideImages(slideToShow);
      slideToShow.classList.add('card-gallery-default__slide--active');
      
      if (isSelected) {
        slideToShow.classList.add('card-gallery-default__slide--swatch-selected');
      }

      // Update dots
      const imageIndex = parseInt(slideToShow.dataset.imageIndex || slideToShow.dataset.slideIndex || '0', 10);
      this.#syncGalleryNavItems(imageIndex);
    } else {
      // If slide with media ID not found, keep showing current slide or show first slide
      // This prevents hiding all images when variant doesn't have assigned image
      const currentSlide = this.slides.find(slide => !slide.hidden);
      if (!currentSlide) {
        // No current slide visible, show first slide as fallback
        this.#showSlideByIndex(0);
      }
      // Otherwise, keep current slide visible (do nothing)
    }
  }

  /**
   * Show a specific slide.
   *
   * Called from arrow/dot click handlers and touch swipe (via
   * `#navigateGallery`). When another slide is currently visible (and
   * reduced motion isn't preferred) a smooth horizontal slide animation is
   * played: the outgoing slide translates off-screen while the incoming
   * slide translates into place. Dots update immediately so the UI feels
   * responsive. State mutations (`--active` class, `hidden` attribute,
   * variant id, etc.) are applied once the animation finishes.
   *
   * NOTE: Card hover does NOT route through this method — hover keeps its
   * existing instant hide/show swap.
   *
   * @param {HTMLElement} slideElement
   */
  #showSlide(slideElement) {
    this.#restoreHoverPreviewState('showSlide');

    // Identify the slide we are transitioning away from. It must be different
    // from the target slide; otherwise we skip animation entirely.
    const previousSlide = this.slides.find(
      (slide) =>
        slide !== slideElement &&
        !slide.hidden &&
        slide.classList.contains('card-gallery-default__slide--active')
    );

    /** Apply the final navigation state (classes, hidden, variant id, dots). */
    const applyFinalState = () => {
      this.slides.forEach((slide) => {
        slide.classList.remove(
          'card-gallery-default__slide--active',
          'card-gallery-default__slide--swatch-selected',
          'card-gallery-default__slide--swatch-hovered',
          'card-gallery-default__slide--card-hover'
        );
        if (slide !== slideElement) {
          slide.hidden = true;
        }
      });

      slideElement.classList.add('card-gallery-default__slide--active');
      slideElement.hidden = false;
      this.#loadSlideImages(slideElement);

      if (slideElement.dataset.variantId) {
        this.#selectedVariantId = slideElement.dataset.variantId;
      }

      const variantId = slideElement.dataset.variantId;
      if (variantId) {
        this.#updateDotsForVariant(variantId);
      } else {
        const slideIndex = Number.parseInt(slideElement.dataset.imageIndex || slideElement.dataset.slideIndex || '0', 10);
        this.#syncGalleryNavItems(slideIndex);
      }
    };

    if (!(previousSlide instanceof HTMLElement)) {
      // Nothing to slide away from; apply the final state immediately.
      applyFinalState();
      return;
    }

    // Infer direction from DOM order so navigation (next/prev/swipe/dot)
    // always slides the visually correct way.
    const slidesInDom = this.slides;
    const previousIdx = slidesInDom.indexOf(previousSlide);
    const targetIdx = slidesInDom.indexOf(slideElement);
    const direction = targetIdx >= previousIdx ? 'next' : 'prev';

    // Update the dot active state immediately for snappy UI feedback while
    // the slide is still mid-transition.
    const targetIndex = parseInt(
      slideElement.dataset.imageIndex || slideElement.dataset.slideIndex || '0',
      10
    );
    const variantIdForDots = slideElement.dataset.variantId;
    this.#updateDotsForVariant(variantIdForDots, targetIndex);

    this.#animateSlideTransition(previousSlide, slideElement, direction, applyFinalState);
  }

  /**
   * Get the variant picker component
   * @returns {SwatchesDefaultVariantPickerComponent | null}
   */
  get variantPicker() {
    return this.querySelector('swatches-default-variant-picker-component');
  }

  /**
   * Get the gallery element
   * @returns {HTMLElement | null}
   */
  get gallery() {
    return this.querySelector('.card-gallery-default');
  }

  /**
   * Get all gallery slides
   * @returns {HTMLElement[]}
   */
  get slides() {
    return Array.from(this.querySelectorAll('.card-gallery-default__slide'));
  }

  /**
   * Get slides for a specific variant ID
   * @param {string} variantId
   * @returns {HTMLElement[]}
   */
  #getSlidesForVariant(variantId) {
    if (!variantId) return [];
    return this.slides.filter(slide => String(slide.dataset.variantId) === String(variantId));
  }

  /**
   * Get currently selected variant ID (from selected swatch or default)
   * @returns {string | null}
   */
  #getSelectedVariantId() {
    if (this.#selectedVariantId) {
      return this.#selectedVariantId;
    }
    
    // Get from selected swatch
    const selectedSwatch = this.querySelector('.swatches-default__swatch--selected input[type="radio"]:checked');
    if (selectedSwatch && selectedSwatch.dataset.variantId) {
      return selectedSwatch.dataset.variantId;
    }
    
    // Get from default visible slide
    const defaultSlide = this.slides.find(slide => 
      !slide.hidden && slide.classList.contains('card-gallery-default__slide--active')
    );
    if (defaultSlide && defaultSlide.dataset.variantId) {
      return defaultSlide.dataset.variantId;
    }
    
    return null;
  }

  /**
   * Handle variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  /**
   * Stop VariantSelectedEvent from OTHER cards in capture phase
   * @param {VariantSelectedEvent} event
   */
  #handleVariantSelectedCapture = (event) => {
    // Stop events from OTHER cards in capture phase before they reach section-level listeners
    const eventTarget = event.target;
    const isEventFromThisCard = eventTarget && this.contains(/** @type {Node} */ (eventTarget));
    
    // If event is not from this card, stop it immediately in capture phase
    if (!isEventFromThisCard) {
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }
    
    // For events from THIS card, allow them to continue to bubble phase
  };

  /**
   * Handle variant selected event
   */
  #handleVariantSelected = (event) => {
    // Stop propagation to prevent event from reaching section level where other cards' components listen
    event.stopPropagation();
    
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handle cart add event - open cart drawer
   * @param {CartAddEvent} event
   */
  #handleCartAdd = (event) => {
    // Check if this add-to-cart came from this product card
    const productFormComponent = this.querySelector('product-form-component');
    if (!productFormComponent) return;
    
    // Match by product ID instead of source ID (sourceId is variant ID, not form element ID)
    const eventProductId = event.detail?.data?.productId;
    const cardProductId = productFormComponent instanceof HTMLElement ? productFormComponent.dataset.productId : '';
    
    if (cardProductId && eventProductId && eventProductId !== cardProductId) return;
    
    const eventSource = event.detail?.data?.source;
    if (eventSource && eventSource !== 'product-form-component') return;
    
    // Reset add to cart in progress flag on successful add
    this.#addToCartInProgress = false;
    if (this.#addToCartTimeout !== null) {
      clearTimeout(this.#addToCartTimeout);
      this.#addToCartTimeout = null;
    }
    
    // Cart drawer will open automatically via auto-open attribute on cart-drawer-component
    // when CartAddEvent is dispatched (handled by product-form-component)
  };

  /**
   * Check if element is in quick view or popup context (should not be handled by product card)
   * @param {HTMLElement} element
   * @returns {boolean}
   */
  #isInOtherContext(element) {
    if (!element) return false;
    return !!(
      element.closest('quick-view-component') ||
      element.closest('drawer-items-component') ||
      element.closest('[data-drawer-footer]') ||
      element.closest('collection-banner-popup-component')
    );
  }

  /**
   * Handle cart error event - show error message
   * @param {CustomEvent} event
   */
  #handleCartError = (event) => {
    if (!(event instanceof CartErrorEvent)) return;
    
    const sourceId = event.detail?.sourceId;
    
    // Check if error is from quick view, popup, or other contexts - if so, ignore it
    if (sourceId) {
      const form = document.getElementById(sourceId);
      if (form) {
        // If form is in other context or not in this product card, don't handle it
        if (this.#isInOtherContext(form) || !this.contains(form)) {
          return;
        }
      }
    }
    
    // Extract error message
    let errorMessage = event.detail?.data?.message || 'Unable to add item to cart';
    const errors = event.detail?.data?.errors;
    
    if (errors) {
      if (errors.items) {
        const itemsError = Array.isArray(errors.items) ? errors.items[0] : errors.items;
        if (itemsError) {
          errorMessage = typeof itemsError === 'string' ? itemsError : (itemsError.message || errorMessage);
        }
      } else if (errors.quantity) {
        const quantityError = Array.isArray(errors.quantity) ? errors.quantity[0] : errors.quantity;
        if (quantityError) {
          errorMessage = typeof quantityError === 'string' ? quantityError : (quantityError.message || errorMessage);
        }
      } else if (Array.isArray(errors) && errors.length > 0) {
        errorMessage = typeof errors[0] === 'string' ? errors[0] : (errors[0].message || errorMessage);
      } else if (errors.message) {
        errorMessage = errors.message;
      }
    }
    
    // Replace specific error phrases with custom message
    const lowerMessage = errorMessage.toLowerCase();
    if (lowerMessage.includes('parameter missing') || lowerMessage.includes('invalid')) {
      errorMessage = 'The maximum quantity of this item is already in your cart.';
    }
    
    // Find button - only from forms within this product card
    let addToCartButton = null;
    if (sourceId) {
      const form = document.getElementById(sourceId);
      if (form && this.contains(form)) {
        addToCartButton = form.querySelector('button[type="submit"][name="add"]');
      }
    }
    
    // Fallback: find processing button in this product card
    if (!addToCartButton) {
      addToCartButton = Array.from(this.querySelectorAll('button[type="submit"][name="add"]'))
        .find(btn => btn.classList.contains('add-to-cart-button--processing'));
    }
    
    // Final validation: ensure button is in this product card and not in other contexts
    if (!addToCartButton || this.#isInOtherContext(addToCartButton) || !this.contains(addToCartButton)) {
      return;
    }
    
    // Reset add to cart in progress flag on error
    this.#addToCartInProgress = false;
    if (this.#addToCartTimeout !== null) {
      clearTimeout(this.#addToCartTimeout);
      this.#addToCartTimeout = null;
    }
    
      // Cancel any active animation
      const animationId = this.#activeAnimations.get(addToCartButton);
      if (animationId) {
        clearTimeout(animationId.checkmarkTimeout);
        clearTimeout(animationId.resetTimeout);
        if (animationId.restoreTimeout !== null) {
          clearTimeout(animationId.restoreTimeout);
        }
        this.#activeAnimations.delete(addToCartButton);
      }
    
    // Reset button state immediately
    addToCartButton.classList.remove('add-to-cart-button--processing');
    const checkmarkElement = addToCartButton.querySelector('.add-to-cart-checkmark');
    if (checkmarkElement) {
      checkmarkElement.classList.remove('add-to-cart-checkmark--visible');
    }
    
    // Restore .add-to-cart-text container
    const addToCartTextContainer = addToCartButton.querySelector('.add-to-cart-text');
    if (addToCartTextContainer) {
      addToCartTextContainer.classList.remove('add-to-cart-text--hidden');
    }
    
    // Show error message
    this.#showAddToCartError(addToCartButton, errorMessage);
};

  /**
   * Show error message above add to cart button
   * @param {HTMLButtonElement} button
   * @param {string} message
   */
  #showAddToCartError(button, message) {
    // Clear any existing error message timeout
    if (this.#errorMessageTimeout !== null) {
      clearTimeout(this.#errorMessageTimeout);
      this.#errorMessageTimeout = null;
    }
    
    // Remove ALL existing error messages within this product card to prevent duplicates
    const existingErrors = this.querySelectorAll('.add-to-cart-error-message');
    existingErrors.forEach(error => error.remove());
    
    // For desktop: find .card-gallery-default div and insert error message AFTER it
    const cardGalleryDefault = button.closest('.card-gallery-default');
    
    // For mobile: find .buy-buttons-default container
    const buyButtonsDefault = button.closest('.buy-buttons-default');
    
    let targetContainer = null;
    let insertAfter = false;
    
    if (cardGalleryDefault) {
      // Desktop: insert AFTER .card-gallery-default
      targetContainer = cardGalleryDefault;
      insertAfter = true;
    } else if (buyButtonsDefault) {
      // Mobile: insert ABOVE .buy-buttons-default
      targetContainer = buyButtonsDefault;
      insertAfter = false;
    }
    
    if (!targetContainer) {
      console.warn('ProductDefaultCard: Could not find target container for error message');
      return;
    }
    
    // Get the parent element for insertion
    const parentElement = targetContainer.parentElement;
    if (!parentElement) {
      console.warn('ProductDefaultCard: Could not find parent element for error message');
      return;
    }
    
    // Create error message element
    const errorElement = document.createElement('div');
    errorElement.className = 'add-to-cart-error-message';
    errorElement.textContent = message;
    errorElement.setAttribute('role', 'alert');
    errorElement.setAttribute('aria-live', 'assertive');
    
    // Insert error message based on container type
    if (insertAfter) {
      // Desktop: insert AFTER .card-gallery-default div
      targetContainer.insertAdjacentElement('afterend', errorElement);
    } else {
      // Mobile: insert ABOVE .buy-buttons-default (as a sibling, before it)
      parentElement.insertBefore(errorElement, targetContainer);
    }
    
    // Auto-hide after 5 seconds
    this.#errorMessageTimeout = setTimeout(() => {
      if (errorElement.parentElement) {
        errorElement.remove();
      }
      this.#errorMessageTimeout = null;
    }, 5000);
  }

  /**
   * Setup checkmark animation for add to cart buttons
   */
  #setupAddToCartCheckmarkAnimation() {
    
    // Listen for cart errors to cancel animations and show error
    const errorHandler = (/** @type {CustomEvent} */ event) => {
      if (!(event instanceof CartErrorEvent)) return;
      
      // Find the button that triggered this error
      const sourceId = event.detail?.sourceId;
      const errorMessage = event.detail?.data?.message || 'Unable to add item to cart';
      
      // Find button by form ID or search in product card context
      let addToCartButton = null;
      if (sourceId) {
        const form = document.getElementById(sourceId);
        if (form && this.contains(form)) {
          addToCartButton = form.querySelector('button[type="submit"][name="add"]');
        }
      }
      
      // Fallback: find button in product card context
      if (!addToCartButton) {
        const buttons = this.querySelectorAll('button[type="submit"][name="add"]');
        // Find the one that's currently processing
        buttons.forEach((btn) => {
          if (btn.classList.contains('add-to-cart-button--processing')) {
            addToCartButton = btn;
          }
        });
      }
      
      // Final validation: ensure button is in this product card and not in other contexts
      if (!addToCartButton || this.#isInOtherContext(addToCartButton) || !this.contains(addToCartButton)) {
        return;
      }
      
      // Cancel any active animation
      const animationId = this.#activeAnimations.get(addToCartButton);
      if (animationId) {
        clearTimeout(animationId.checkmarkTimeout);
        clearTimeout(animationId.resetTimeout);
        if (animationId.restoreTimeout !== null) {
          clearTimeout(animationId.restoreTimeout);
        }
        this.#activeAnimations.delete(addToCartButton);
      }
      
      // Reset button state immediately
      addToCartButton.classList.remove('add-to-cart-button--processing');
      const checkmarkElement = addToCartButton.querySelector('.add-to-cart-checkmark');
      if (checkmarkElement) {
        checkmarkElement.classList.remove('add-to-cart-checkmark--visible');
      }
      
      // Restore the entire .add-to-cart-text container (which contains both icon and text)
      const addToCartTextContainer = addToCartButton.querySelector('.add-to-cart-text');
      if (addToCartTextContainer) {
        addToCartTextContainer.classList.remove('add-to-cart-text--hidden');
      }
      
      // Show error message
      this.#showAddToCartError(addToCartButton, errorMessage);
    };
    
    document.addEventListener(ThemeEvents.cartError, errorHandler);
    
    // Use event delegation to handle clicks on add-to-cart buttons
    this.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      const addToCartButton = /** @type {HTMLButtonElement | null} */ (
        target.closest('button[type="submit"][name="add"]')
      );
      
      if (!addToCartButton || addToCartButton.disabled) return;
      
      // Only handle if button is within this product card
      if (!this.contains(addToCartButton) || this.#isInOtherContext(addToCartButton)) return;

      // Find the .add-to-cart-text container (contains both icon and text)
      const addToCartTextContainer = addToCartButton.querySelector('.add-to-cart-text');
      if (!addToCartTextContainer) return;

      // Check if checkmark already exists
      let checkmarkElement = /** @type {HTMLElement | null} */ (
        addToCartButton.querySelector('.add-to-cart-checkmark')
      );

      if (!checkmarkElement) {
        // Create checkmark element
        checkmarkElement = document.createElement('span');
        checkmarkElement.className = 'add-to-cart-checkmark';
        checkmarkElement.innerHTML = `
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 6L9 17l-5-5"></path>
          </svg>
        `;
        addToCartButton.appendChild(checkmarkElement);
      }

      // Hide .add-to-cart-text container (contains both icon and text)
      addToCartTextContainer.classList.add('add-to-cart-text--hidden');
      addToCartButton.classList.add('add-to-cart-button--processing');

      // Store timeout IDs to allow cancellation
      const checkmarkTimeout = setTimeout(() => {
        checkmarkElement.classList.add('add-to-cart-checkmark--visible');
      }, 300); // Checkmark appears after 0.3s

      const resetTimeout = setTimeout(() => {
        checkmarkElement.classList.remove('add-to-cart-checkmark--visible');

        const restoreTimeout = setTimeout(() => {
          addToCartButton.classList.remove('add-to-cart-button--processing');
          addToCartTextContainer.classList.remove('add-to-cart-text--hidden');
          this.#activeAnimations.delete(addToCartButton);
        }, 1000);
        
        // Update with restore timeout
        const current = this.#activeAnimations.get(addToCartButton);
        if (current) {
          current.restoreTimeout = restoreTimeout;
        }
      }, 3000);
      
      this.#activeAnimations.set(addToCartButton, {
        checkmarkTimeout,
        resetTimeout,
        restoreTimeout: null
      });
    }, true); // Use capture phase
  }

  /**
   * Setup form submission debounce to prevent multiple API calls
   */
  #setupFormSubmissionDebounce() {
    // Listen for form submit events to prevent multiple submissions
    this.addEventListener('submit', (/** @type {Event} */ event) => {
      const form = /** @type {HTMLFormElement | null} */ (event.target);
      if (!form || !form.closest('product-form-component')) return;
      
      // Check if request is already in progress
      if (this.#addToCartInProgress) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      
      // Set flag to prevent multiple submissions
      this.#addToCartInProgress = true;
      
      // Clear any existing timeout
      if (this.#addToCartTimeout !== null) {
        clearTimeout(this.#addToCartTimeout);
      }
      
      // Set timeout fallback to reset flag after 10 seconds (in case API call hangs)
      this.#addToCartTimeout = setTimeout(() => {
        this.#addToCartInProgress = false;
        this.#addToCartTimeout = null;
      }, 10000);
    }, true); // Use capture phase to catch before form submission
  }

  /**
   * Handle variant update event in capture phase - stops events from OTHER cards
   * For events from THIS card, we allow them to pass to bubble phase for processing
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdateCapture = (event) => {
    // CRITICAL: Stop events from OTHER cards in capture phase before they reach section-level listeners
    // Events from THIS card are allowed to continue to bubble phase for processing
    
    // First check: Is the event target within this card?
    const eventTarget = event.target;
    const isEventFromThisCard = eventTarget && this.contains(/** @type {Node} */ (eventTarget));
    
    // If event is not from this card, stop it immediately in capture phase
    // This prevents it from even reaching bubble phase or section-level listeners
    if (!isEventFromThisCard) {
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }
    
    if (!event.detail?.data) return;
    
    // Second check: Does the productId match?
    // Convert to strings for reliable comparison (productId might be number or string)
    const eventProductId = String(event.detail.data.productId || '');
    const currentProductId = String(this.dataset.productId || '');
    
    // Stop events from OTHER product cards immediately in capture phase
    // This prevents them from even reaching the bubble phase or section level
    if (eventProductId && currentProductId && eventProductId !== currentProductId) {
      // Event is for a different product card, stop it immediately
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }
    
    // Also stop if one has productId but the other doesn't (mismatch scenario)
    if ((eventProductId && !currentProductId) || (!eventProductId && currentProductId)) {
      // Mismatch - stop the event to prevent affecting other cards
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }
    
    // For events from THIS card with matching productId, allow them to continue
    // They will be processed in bubble phase and stopped there to prevent reaching section
  };

  /**
   * Handle variant update event
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (!event.detail?.data) return;

    // CRITICAL: Stop propagation IMMEDIATELY (FIRST THING) to prevent event from reaching section level
    // where other cards' product-form-components listen. This matches the standard product-card.js pattern.
    // This fixes the issue where selecting a swatch on one product disables add-to-cart buttons on all other products.
    event.stopPropagation();

    // Ensure this event is for this product card only
    // Convert to strings for reliable comparison (productId might be number or string)
    const eventProductId = String(event.detail.data.productId || '');
    const currentProductId = String(this.dataset.productId || '');
    
    // If this event is for a different product card, return early
    // (Capture phase should have already stopped these, but double-check for safety)
    if (eventProductId && currentProductId && eventProductId !== currentProductId) {
      return;
    }

    const { html } = event.detail.data;
    const variantResource = event.detail.resource;

    this.#restoreHoverPreviewState('handleVariantUpdate');
    this.#swatchSelected = true;
    this.#clearSwatchHoverState();

    // Every block below is updated independently: a failure in one (e.g. a card without a title block,
    // or markup that morph cannot reconcile) must never prevent the remaining blocks from updating.
    const newVariantId = this.#getResponseVariantId(html, variantResource);

    // Update product URL
    this.#safeUpdate('productUrl', () => this.#updateProductUrl(html));

    // Update gallery images via morph (replaces manual #updateVariantImages)
    this.#safeUpdate('galleryImages', () => this.#updateGalleryImages(html));

    // Update form variant IDs
    this.#safeUpdate('formVariantIds', () => this.#updateFormVariantIds(html));

    // Update price
    this.#safeUpdate('price', () => this.#updatePrice(html));

    // Update badges
    this.#safeUpdate('badges', () => this.#updateBadges(html));

    // Update shipping badge
    this.#safeUpdate('shippingBadge', () => this.#updateShippingBadge(html));

    // Update ratings (variant-specific Yotpo)
    this.#safeUpdate('ratings', () => this.#updateRatings(html));

    // Update product title (variant metafield name from section HTML / resource)
    this.#safeUpdate('productTitle', () => this.#updateProductTitle(html, variantResource));

    // Update wishlist heart (variant-level wishlist: data-variant-id drives the in-wishlist state)
    this.#safeUpdate('wishlist', () => this.#updateWishlist(html, newVariantId));

    // Point quick view triggers (Quick Add / +X) at the selected variant so the drawer opens on it
    this.#safeUpdate('quickViewTriggers', () => this.#updateQuickViewTriggers(newVariantId));

    // Update selected swatch visual state
    this.#safeUpdate('swatchSelectedState', () => this.#updateSwatchSelectedState());

    // CRITICAL: Manually update product-form-component button state
    // This ensures the button state is correct even if the event doesn't reach product-form-component
    // or if product-form-component's handler doesn't run properly
    // Use requestAnimationFrame to ensure this runs after product-form-component's handler (if it runs)
    requestAnimationFrame(() => {
      this.#updateProductFormComponent(html, variantResource);
    });

    // CRITICAL: Stop propagation AFTER processing to prevent event from reaching section level
    // where other cards' product-form-components listen. 
    // The capture phase already stops events from OTHER cards, so this only stops events from THIS card
    // from bubbling to the section level. This allows product-form-component on THIS card to receive the event.
    // NOTE: We use stopPropagation() (not stopImmediatePropagation()) so that other handlers on the same element
    // (like gallery's product-form-component) can still receive and process the event
    event.stopPropagation();
  };

  /**
   * Run one block update in isolation so a single failing block cannot abort the others.
   * @param {string} name
   * @param {() => void} fn
   */
  #safeUpdate(name, fn) {
    try {
      fn();
    } catch (error) {
      console.warn(`ProductDefaultCard: failed to update ${name} after variant change`, error);
    }
  }

  /**
   * Resolve the selected variant id from the section rendering response (or the variant resource).
   * @param {Document} html
   * @param {any | null} variantResource
   * @returns {string | null}
   */
  #getResponseVariantId(html, variantResource) {
    const input = html?.querySelector('product-default-card input[name="id"]') || html?.querySelector('input[name="id"]');
    if (input instanceof HTMLInputElement && input.value) return input.value;
    if (variantResource?.id) return String(variantResource.id);
    const slide = html?.querySelector('.card-gallery-default__slides-wrapper[data-variant-id]');
    if (slide instanceof HTMLElement && slide.dataset.variantId) return slide.dataset.variantId;
    return null;
  }

  /**
   * Keep the card's wishlist heart in sync with the selected variant.
   * The wishlist is variant based: <wishlist-button> re-renders its state whenever data-variant-id changes.
   * @param {Document} html
   * @param {string | null} newVariantId
   */
  #updateWishlist(html, newVariantId) {
    const buttons = this.querySelectorAll('wishlist-button');
    if (!buttons.length) return;

    const source =
      html?.querySelector('product-default-card [data-wishlist-variant]') ||
      html?.querySelector('[data-wishlist-variant]') ||
      html?.querySelector('product-default-card wishlist-button') ||
      html?.querySelector('wishlist-button');

    const variantId = (source instanceof HTMLElement && source.dataset.variantId) || newVariantId;
    if (!variantId) return;
    const sku = source instanceof HTMLElement ? source.dataset.sku : undefined;

    buttons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      if (button.dataset.productId && this.dataset.productId && button.dataset.productId !== this.dataset.productId) return;
      if (button.getAttribute('data-variant-id') !== variantId) {
        // setAttribute (not dataset) so attributeChangedCallback fires reliably in every browser
        button.setAttribute('data-variant-id', variantId);
      }
      if (sku !== undefined) {
        button.setAttribute('data-sku', sku);
      }
    });
  }

  /**
   * Point every quick view trigger in this card at the selected variant, so Quick Add / +X open
   * the drawer on the same variant the shopper selected on the card.
   * @param {string | null} newVariantId
   */
  #updateQuickViewTriggers(newVariantId) {
    if (!newVariantId) return;
    this.querySelectorAll('[data-open-quick-view], [data-quick-view-trigger]').forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;
      if (trigger.dataset.productId && this.dataset.productId && trigger.dataset.productId !== this.dataset.productId) return;
      trigger.dataset.variantId = newVariantId;
    });
  }

  /**
   * Update product URLs
   * @param {Document} html
   */
  #updateProductUrl(html) {
    const anchorElement = html?.querySelector('product-default-card a[ref="productCardLink"]');

    if (!(anchorElement instanceof HTMLAnchorElement)) return;
    if (anchorElement.getAttribute('href')?.trim() === '') return;

    let productUrl = anchorElement.href;
    if (this.dataset.variantSliced === 'true') {
      const nextUrl = new URL(productUrl, window.location.origin);
      const currentHref = this.refs.productCardLink?.href;
      if (!nextUrl.searchParams.get('variant') && currentHref) {
        const currentVariant = new URL(currentHref, window.location.origin).searchParams.get('variant');
        if (currentVariant) {
          nextUrl.searchParams.set('variant', currentVariant);
          productUrl = nextUrl.toString();
        }
      }
    }
    const { productCardLink, cardGalleryLink, productTitleLink } = this.refs;

    if (productCardLink instanceof HTMLAnchorElement) {
      productCardLink.href = productUrl;
    }
    if (cardGalleryLink instanceof HTMLAnchorElement) {
      cardGalleryLink.href = productUrl;
    }
    if (productTitleLink instanceof HTMLAnchorElement) {
      productTitleLink.href = productUrl;
    }
  }

  /**
   * Update gallery images via morph (replaces manual image updates)
   * @param {Document} html
   */
  #updateGalleryImages(html) {
    // Find the new gallery slides wrapper from section rendering response
    const newSlidesWrapper = html?.querySelector('.card-gallery-default__slides-wrapper');
    
    if (!newSlidesWrapper) {
      console.warn('ProductDefaultCard: No gallery slides wrapper found in section rendering response');
      return;
    }

    // Find current gallery slides wrapper
    const currentSlidesWrapper = this.querySelector('.card-gallery-default__slides-wrapper');
    
    if (!currentSlidesWrapper) {
      console.warn('ProductDefaultCard: No current gallery slides wrapper found');
      return;
    }

    // Use morph to update the gallery images
    morph(currentSlidesWrapper, newSlidesWrapper);

    // After morph, update selected variant ID and reset navigation state
    const firstVisibleSlide = this.slides.find(slide => !slide.hidden);
    if (firstVisibleSlide && firstVisibleSlide.dataset.variantId) {
      this.#selectedVariantId = firstVisibleSlide.dataset.variantId;
    }

    // Update dots and arrows visibility for the new variant
    if (this.#selectedVariantId) {
      this.#updateDotsForVariant(this.#selectedVariantId);
    }
    this.#updateNavArrowsVisibility();
  }

  /**
   * Update form variant IDs
   * @param {Document} html
   */
  #updateFormVariantIds(html) {
    const newVariantInput = html?.querySelector('input[name="id"]');
    if (!newVariantInput) return;

    const newVariantId = newVariantInput.value;

    // Update all variant ID inputs in forms (gallery form, mobile form, Shop Pay form)
    const variantInputs = this.querySelectorAll('input[name="id"]');
    variantInputs.forEach((input) => {
      input.value = newVariantId;
    });

    // Also update ref="variantId" inputs (used by product-form-component)
    const variantIdRefs = this.querySelectorAll('[ref="variantId"]');
    variantIdRefs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.value = newVariantId;
      }
    });
  }

  /**
   * Update price display
   * @param {Document} html
   */
  #updatePrice(html) {
    // Try to find price block in the section rendering response
    const newPrice = html?.querySelector('.price-default');
    
    if (!newPrice) {
      // If not found in section rendering, try to find it in the product-default-card wrapper
      const productCard = html?.querySelector('product-default-card');
      if (productCard) {
        const priceInCard = productCard.querySelector('.price-default');
        if (priceInCard) {
          this.#morphPriceElement(priceInCard);
          return;
        }
      }
      return;
    }

    this.#morphPriceElement(newPrice);
  }

  /**
   * Morph the price element with new content
   * @param {HTMLElement} newPrice - The new price element from the response
   */
  #morphPriceElement(newPrice) {
    const currentPrice = this.querySelector('.price-default');
    
    if (!currentPrice) return;

    // Store the merchant (block) settings BEFORE morphing - the section response always renders with them enabled
    const showBudgetPay = currentPrice.dataset.showBudgetPay === 'true';
    const showDiscountPercentage = currentPrice.dataset.showDiscountPercentage === 'true';
    // Older markup without the attribute: keep the previous behaviour (clearance colour on)
    const enableClearancePriceColor = currentPrice.dataset.enableClearancePriceColor !== 'false';

    // Store the price range element BEFORE morphing (it should remain static)
    const priceRangeElement = currentPrice.querySelector('.price-default__range');
    const priceRangeClone = priceRangeElement?.cloneNode(true);

    // IMPORTANT: Morph FIRST (morph modifies currentPrice in place)
    morph(currentPrice, newPrice);

    // Use requestAnimationFrame to ensure DOM is updated after morph
    requestAnimationFrame(() => {
      // After morphing, currentPrice IS the updated element (morph modifies in place)
      // Update data attributes to preserve settings for future updates
      currentPrice.dataset.showBudgetPay = String(showBudgetPay);
      currentPrice.dataset.showDiscountPercentage = String(showDiscountPercentage);
      currentPrice.dataset.enableClearancePriceColor = String(enableClearancePriceColor);

      // Clearance (red) price colour is a block setting; the response renders it on, so strip it when disabled
      if (!enableClearancePriceColor) {
        currentPrice.querySelectorAll('.price-default__current--clearance').forEach((el) => {
          el.classList.remove('price-default__current--clearance');
        });
      }

      // Restore price range if it existed before (price range is static and shouldn't change on variant selection)
      if (priceRangeClone instanceof HTMLElement) {
        const existingRange = currentPrice.querySelector('.price-default__range');
        const mainPriceElement = currentPrice.querySelector('.price-default__main');
        
        if (!existingRange && mainPriceElement instanceof HTMLElement) {
          // Insert price range before the main price element
          mainPriceElement.insertAdjacentElement('beforebegin', priceRangeClone);
        } else if (existingRange) {
          // If range exists in new content, keep it (it should be the same anyway)
          // But we could replace it with the original if needed for consistency
        }
      }

      // Hide/show Budget Pay based on setting
      const budgetPayElement = currentPrice.querySelector('[data-budget-pay]');
      if (budgetPayElement) {
        budgetPayElement.style.display = showBudgetPay ? '' : 'none';
      }

      // Hide/show Discount Percentage based on setting
      const discountElement = currentPrice.querySelector('[data-discount]');
      if (discountElement) {
        discountElement.style.display = showDiscountPercentage ? '' : 'none';
      }

      // Copy other data attributes from newPrice (if morph didn't copy them)
      if (newPrice.dataset.installments) {
        currentPrice.dataset.installments = newPrice.dataset.installments;
      }
      if (newPrice.dataset.price) {
        currentPrice.dataset.price = newPrice.dataset.price;
      }
      if (newPrice.dataset.compareAtPrice) {
        currentPrice.dataset.compareAtPrice = newPrice.dataset.compareAtPrice;
      }
      if (newPrice.dataset.variantId) {
        currentPrice.dataset.variantId = newPrice.dataset.variantId;
      }
    });
  }
  /**
   * Update badges display
   * @param {Document} html
   */
  #updateBadges(html) {
    // Try to find badges in the section rendering response
    const newBadges = html?.querySelector('.product-default-badges');
    
    if (!newBadges) {
      // If not found in section rendering, try to find it in the product-default-card wrapper
      const productCard = html?.querySelector('product-default-card');
      if (productCard) {
        const badgesInCard = productCard.querySelector('.product-default-badges');
        if (badgesInCard) {
          this.#morphBadgesElement(badgesInCard);
          return;
        }
      }
      // If no badges in response, remove existing badges from gallery
      const gallery = this.gallery;
      if (gallery) {
        const currentBadges = gallery.querySelector('.product-default-badges');
        if (currentBadges) {
          currentBadges.remove();
        }
      }
      return;
    }

    this.#morphBadgesElement(newBadges);
  }

  /**
   * Morph the badges element with new content
   * @param {HTMLElement} newBadges - The new badges element from the response
   */
  #morphBadgesElement(newBadges) {
    // Badges are rendered inside the gallery block
    const gallery = this.gallery;
    if (!gallery) return;

    const currentBadges = /** @type {HTMLElement | null} */ (gallery.querySelector('.product-default-badges'));
    
    if (!currentBadges) {
      // If badges don't exist, append them to the gallery
      if (newBadges instanceof HTMLElement) {
        gallery.insertAdjacentElement('afterbegin', newBadges.cloneNode(true));
      }
      return;
    }

    // Use morph to update the badges element
    if (newBadges instanceof HTMLElement) {
      morph(currentBadges, newBadges);
    }
  }

  /**
   * Update shipping badge display
   * @param {Document} html
   */
  #updateShippingBadge(html) {
    // Find the shippingbadge-block wrapper in current card (always exists when block is added)
    const shippingBadgeBlock = /** @type {HTMLElement | null} */ (this.querySelector('.shippingbadge-block'));
    
    if (!shippingBadgeBlock) {
      // If block wrapper doesn't exist, nothing to update
      return;
    }

    // Try to find shipping badge in the section rendering response
    // It could be inside [data-shipping-badge] wrapper or directly in product-default-card
    const productCard = html?.querySelector('product-default-card');
    const dataWrapper = html?.querySelector('[data-shipping-badge]');
    const newShippingBadge = dataWrapper?.querySelector('.shippingbadge') 
      || productCard?.querySelector('.shippingbadge')
      || html?.querySelector('.shippingbadge');
    
    const currentShippingBadge = /** @type {HTMLElement | null} */ (shippingBadgeBlock.querySelector('.shippingbadge'));
    
    if (!newShippingBadge) {
      // If no shipping badge in response, remove existing badge from block wrapper
      if (currentShippingBadge) {
        currentShippingBadge.remove();
      }
      return;
    }

    // Update or add the shipping badge inside the block wrapper
    if (!currentShippingBadge) {
      // Badge doesn't exist in current card, append it inside the block wrapper
      if (newShippingBadge instanceof HTMLElement) {
        shippingBadgeBlock.appendChild(newShippingBadge.cloneNode(true));
      }
    } else {
      // Badge exists, morph it with new content
      if (newShippingBadge instanceof HTMLElement) {
        morph(currentShippingBadge, newShippingBadge);
      }
    }
  }

  /**
   * Update ratings block from section rendering response (variant-specific Yotpo).
   * Response always includes .product-default-ratings-wrapper (empty state has --empty class).
   * Morphs wrapper into card, then shows/hides ratings area based on empty state.
   * @param {Document} html - Section rendering API response
   */
  #updateRatings(html) {
    if (!html) return;
    const productId = this.dataset.productId;
    let productCard = productId
      ? html.querySelector(`product-default-card[data-product-id="${productId}"]`)
      : null;
    if (!productCard) {
      productCard = html.querySelector('product-default-card');
    }
    const updateBlock = productCard?.querySelector('.product-default-ratings-update');
    let newWrapper = updateBlock?.querySelector('.product-default-ratings-wrapper');
    if (!newWrapper) {
      newWrapper = html.querySelector('.product-default-ratings-wrapper');
    }
    const ratingsContainers = this.querySelectorAll('.card-gallery-default__ratings');
    const currentWrappers = this.querySelectorAll('.product-default-ratings-wrapper');

    const isEmpty =
      !(newWrapper instanceof HTMLElement) ||
      newWrapper.classList.contains('product-default-ratings-wrapper--empty') ||
      newWrapper.getAttribute('data-review-count') === '0';

    if (newWrapper instanceof HTMLElement) {
      if (currentWrappers.length > 0) {
        currentWrappers.forEach((current) => {
          if (current instanceof HTMLElement) {
            morph(current, newWrapper.cloneNode(true));
          }
        });
      } else {
        ratingsContainers.forEach((container) => {
          if (container instanceof HTMLElement) {
            container.appendChild(newWrapper.cloneNode(true));
          }
        });
      }
    }

    ratingsContainers.forEach((el) => {
      if (el instanceof HTMLElement) {
        el.hidden = isEmpty;
      }
    });
  }

  /**
   * Update product title based on variant metafield name
   * Supports both product-title and product-title-default block structures
   * @param {Document} html
   * @param {any | null} variantResource
   */
  #updateProductTitle(html, variantResource) {
    // Clickable titles expose ref="productTitleLink"; non-clickable titles render a plain span.
    const productTitleLink =
      this.refs.productTitleLink ||
      this.querySelector('.product-title-default__link, .product-meta-title__link, .product-title-default__text-content, .product-meta-title__text-content');
    if (!productTitleLink) return;

    const productId = this.dataset.productId;
    let productCard = productId
      ? html?.querySelector(`product-default-card[data-product-id="${productId}"]`)
      : null;
    if (!productCard) {
      productCard = html?.querySelector('product-default-card');
    }

    const newTitleLink =
      productCard?.querySelector('.product-title-link, .product-title-default__link') ||
      html?.querySelector('.product-title-link, .product-title-default__link');

    if (newTitleLink instanceof HTMLAnchorElement && productTitleLink instanceof HTMLAnchorElement) {
      productTitleLink.href = newTitleLink.href;
    }

    let newTitle = '';
    if (newTitleLink) {
      const titleTextElement = newTitleLink.querySelector('.product-title-text');
      if (titleTextElement) {
        const paragraph = titleTextElement.querySelector('p');
        newTitle = (paragraph?.textContent || '').trim();
      } else {
        newTitle = (newTitleLink.textContent || '').trim();
      }
      if (!newTitle && newTitleLink instanceof HTMLElement && newTitleLink.dataset.productTitle) {
        newTitle = newTitleLink.dataset.productTitle.trim();
      }
    }

    if (!newTitle && variantResource?.metafields?.custom) {
      const { name, variant_name } = variantResource.metafields.custom;
      newTitle = name?.value?.trim() || variant_name?.value?.trim() || '';
    }

    if (!newTitle && productTitleLink instanceof HTMLElement) {
      newTitle = (productTitleLink.dataset.productTitle || '').trim();
    }

    if (newTitle) {
      const richTextTitle = this.querySelector('.product-title-text');
      if (richTextTitle) {
        // Legacy product-title block: text lives in a nested rich-text paragraph
        const paragraph = richTextTitle.querySelector('p') || richTextTitle.querySelector('.text-block p');
        if (paragraph) {
          paragraph.textContent = newTitle;
        }
      } else if (productTitleLink instanceof HTMLElement) {
        // product-title-default / product-meta-title (link or plain span): plain text content
        productTitleLink.textContent = newTitle;
        productTitleLink.dataset.productTitle = newTitle;
      }
    }
  }

  /**
   * Update swatch selected visual state
   */
  #updateSwatchSelectedState() {
    if (!this.variantPicker) return;

    const allSwatches = this.querySelectorAll('.swatches-default__swatch');
    const selectedInput = this.variantPicker.selectedOption;

    allSwatches.forEach((swatch) => {
      swatch.classList.remove('swatches-default__swatch--selected');
    });

    if (selectedInput) {
      const selectedLabel = selectedInput.closest('.swatches-default__swatch');
      if (selectedLabel) {
        selectedLabel.classList.add('swatches-default__swatch--selected');
      }
    }
  }

  /**
   * Manually update ALL product-form-components for current card only
   * This prevents the variant update event from affecting other cards' buttons
   * @param {Document} html - The HTML response from the variant update
   * @param {any | null} variantResource - The variant resource from the event
   */
  #updateProductFormComponent(html, variantResource) {
    // Find ALL product-form-components within this card (gallery and main content)
    const productFormComponents = /** @type {NodeListOf<any>} */ (this.querySelectorAll('product-form-component'));
    if (!productFormComponents || productFormComponents.length === 0) return;

    // Extract variant ID from HTML if variantResource is not available
    let variantId = variantResource?.id;
    if (!variantId && html) {
      const newVariantInput = html.querySelector('input[name="id"]');
      if (newVariantInput) {
        variantId = newVariantInput.value;
      }
    }

    // Update each product-form-component instance
    productFormComponents.forEach((productFormComponent) => {
      // Update variant ID - ensure it's set even if variantResource is missing
      const variantIdRef = productFormComponent.refs?.variantId;
      if (variantIdRef && variantId) {
        variantIdRef.value = variantId;
      }

      // Find the add-to-cart button container
      const addToCartButtonContainer = productFormComponent.refs?.addToCartButtonContainer;
      const currentAddToCartButton = addToCartButtonContainer?.refs?.addToCartButton;
      const acceleratedCheckoutButtonContainer = productFormComponent.refs?.acceleratedCheckoutButtonContainer;

      if (!addToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) {
        return;
      }

      // Update button state based on variant availability
      // Only disable if variant is explicitly unavailable
      // Check variantResource first, then fallback to HTML button state
      let shouldDisable = false;
      
      if (variantResource != null) {
        // We have variant resource - check availability directly
        if (variantResource.available == false) {
          // Variant is explicitly unavailable
          shouldDisable = true;
        } else {
          // Variant is available (available is true or truthy)
          shouldDisable = false;
        }
      } else if (html) {
        // No variantResource - check HTML response button state
        const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');
        if (newAddToCartButton && newAddToCartButton.hasAttribute('disabled')) {
          // Button in HTML is disabled - variant is unavailable
          shouldDisable = true;
        } else {
          // Button in HTML is not disabled - variant is available (or default state)
          shouldDisable = false;
        }
      } else {
        // No variantResource and no HTML - don't change button state
        // Keep current state (don't disable or enable)
        return;
      }

      // Update button state
      if (shouldDisable) {
        addToCartButtonContainer.disable();
        if (acceleratedCheckoutButtonContainer) {
          acceleratedCheckoutButtonContainer.setAttribute('hidden', 'true');
        }
        
        // Disable Shop Pay button wrapper (for product cards)
        // Shop Pay button is a sibling of product-form-component, so search in the product card
        const shopPayWrapper = this.querySelector('.card-gallery-default__shop-pay-wrapper, .buy-shop-button__shoppay');
        if (shopPayWrapper instanceof HTMLElement) {
          shopPayWrapper.classList.add('card-gallery-default__shop-pay-wrapper--disabled', 'buy-shop-button__shoppay--disabled');
          // Also disable the actual Shop Pay button if it exists
          const shopPayButton = shopPayWrapper.querySelector('shopify-accelerated-checkout, button.shopify-payment-button__button');
          if (shopPayButton instanceof HTMLElement) {
            if (shopPayButton instanceof HTMLButtonElement) {
              shopPayButton.disabled = true;
            } else {
              // For web components, set pointer-events and opacity
              shopPayButton.style.pointerEvents = 'none';
              shopPayButton.style.opacity = '0.5';
            }
          }
        }
      } else {
        // Enable button if variant is available
        addToCartButtonContainer.enable();
        if (acceleratedCheckoutButtonContainer) {
          acceleratedCheckoutButtonContainer.removeAttribute('hidden');
        }
        
        // Enable Shop Pay button wrapper (for product cards)
        // Shop Pay button is a sibling of product-form-component, so search in the product card
        const shopPayWrapper = this.querySelector('.card-gallery-default__shop-pay-wrapper, .buy-shop-button__shoppay');
        if (shopPayWrapper instanceof HTMLElement) {
          shopPayWrapper.classList.remove('card-gallery-default__shop-pay-wrapper--disabled', 'buy-shop-button__shoppay--disabled');
          // Also enable the actual Shop Pay button if it exists
          const shopPayButton = shopPayWrapper.querySelector('shopify-accelerated-checkout, button.shopify-payment-button__button');
          if (shopPayButton instanceof HTMLElement) {
            if (shopPayButton instanceof HTMLButtonElement) {
              shopPayButton.disabled = false;
            } else {
              // For web components, restore pointer-events and opacity
              shopPayButton.style.pointerEvents = '';
              shopPayButton.style.opacity = '';
            }
          }
        }
      }

      // Update button text based on availability
      const textEl = productFormComponent.querySelector('[data-add-text]');
      if (textEl instanceof HTMLElement) {
        if (shouldDisable) {
          textEl.textContent = /** @type {any} */ (window.theme)?.strings?.soldOut || 'Sold Out';
        } else {
          textEl.textContent = /** @type {any} */ (window.theme)?.strings?.addToCart || 'Add To Cart';
        }
      }

      // Morph the button if new button HTML is available
      if (currentAddToCartButton && html) {
        const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');
        if (newAddToCartButton) {
          morph(currentAddToCartButton, newAddToCartButton);
        }
      }
    });
  }

  /**
   * Preview a variant (for hover) - copy swatch image into the currently visible gallery slide
   * @param {string} mediaId - The media ID to preview
   */
  previewVariant(mediaId) {
    if (!this.gallery || mediaId == null || mediaId === '') return;

    // Restore any previous hover state that wasn’t reverted (so we don’t stack wrong images)
    this.#restoreHoverPreviewState('previewVariant');

    const mediaIdStr = String(mediaId).trim();
    this.#navigationUsed = false;
    this.#hoveredSwatchMediaId = mediaIdStr;

    if (this.resetVariant && typeof this.resetVariant.cancel === 'function') {
      this.resetVariant.cancel();
    }

    const variantPicker = this.variantPicker;
    if (!variantPicker) return;

    // Find swatch: exact input match, then loose match (numeric ID as string), then label
    let swatchLabel = variantPicker.querySelector(`input[data-option-media-id="${mediaIdStr}"]`)?.closest('label');
    if (!swatchLabel) {
      for (const input of variantPicker.querySelectorAll('input[data-option-media-id]')) {
        const attr = (input.getAttribute('data-option-media-id') || '').trim();
        if (attr === mediaIdStr || String(attr) === String(mediaIdStr)) {
          swatchLabel = input.closest('label');
          break;
        }
      }
    }
    if (!swatchLabel) {
      swatchLabel = variantPicker.querySelector(`label[data-media-id="${mediaIdStr}"]`);
    }
    if (!swatchLabel) return;

    const swatchImage = swatchLabel.querySelector('.swatches-default__swatch-image') ?? swatchLabel.querySelector('.swatches-default__swatch-content img');
    if (!swatchImage) return;
    const swatchSrc = swatchImage.src || swatchImage.getAttribute('src');
    if (!swatchSrc) return;

    const currentSlide = this.slides.find(slide => !slide.hidden);
    if (!currentSlide) return;
    const galleryImage = currentSlide.querySelector('.card-gallery-default__image') ?? currentSlide.querySelector('img');
    if (!galleryImage) return;

    const currentGallerySrc = galleryImage.src || galleryImage.getAttribute('src') || '';
    const currentSrcBase = (currentGallerySrc.split('?')[0] || '').trim();
    const swatchSrcBase = (swatchSrc.split('?')[0] || '').trim();
    if (currentSrcBase && swatchSrcBase && currentSrcBase === swatchSrcBase) return;

    galleryImage.dataset.originalSrc = currentGallerySrc;
    galleryImage.dataset.originalAlt = galleryImage.alt || '';

    // When gallery uses Shopify variant image it has srcset; browser prefers srcset over src.
    // Store and remove srcset/sizes so the new src is used for swatch hover preview.
    const origSrcset = galleryImage.getAttribute('srcset');
    const origSizes = galleryImage.getAttribute('sizes');
    if (origSrcset != null) {
      galleryImage.dataset.originalSrcset = origSrcset;
      galleryImage.removeAttribute('srcset');
    }
    if (origSizes != null) {
      galleryImage.dataset.originalSizes = origSizes;
      galleryImage.removeAttribute('sizes');
    }

    const higherResSrc = swatchSrc.replace(/\bwidth=100\b/, 'width=600').replace(/_100x\b/, '_600x');
    galleryImage.src = higherResSrc || swatchSrc;
    galleryImage.alt = swatchImage.alt || '';

    currentSlide.classList.add('card-gallery-default__slide--swatch-hovered');
  }

  /**
   * Clear swatch hover state (classes and ref only; does not restore img src)
   */
  #clearSwatchHoverState() {
    this.#hoveredSwatchMediaId = null;
    this.slides.forEach((slide) => {
      slide.classList.remove('card-gallery-default__slide--swatch-hovered');
      slide.classList.remove('card-gallery-default__slide--card-hover');
    });
  }

  /**
   * Restore gallery images that were left in hover-preview state (wrong image) when
   * resetVariant was not called (e.g. pointer left card quickly, or event didn't fire).
   * Call before navigation, variant change, or new hover so the wrong image never persists.
   * @param {string} [trigger] - For debug: where this was called from
   * @returns {boolean} - True if any slide was restored
   */
  #restoreHoverPreviewState(trigger = '') {
    let restored = false;
    for (const slide of this.slides) {
      const galleryImage = slide.querySelector('.card-gallery-default__image') ?? slide.querySelector('img');
      if (!galleryImage?.dataset.originalSrc) continue;
      galleryImage.src = galleryImage.dataset.originalSrc;
      galleryImage.alt = galleryImage.dataset.originalAlt || '';
      if (galleryImage.dataset.originalSrcset != null) {
        galleryImage.setAttribute('srcset', galleryImage.dataset.originalSrcset);
        delete galleryImage.dataset.originalSrcset;
      }
      if (galleryImage.dataset.originalSizes != null) {
        galleryImage.setAttribute('sizes', galleryImage.dataset.originalSizes);
        delete galleryImage.dataset.originalSizes;
      }
      delete galleryImage.dataset.originalSrc;
      delete galleryImage.dataset.originalAlt;
      slide.classList.remove('card-gallery-default__slide--swatch-hovered');
      restored = true;
    }
    if (restored && (this.dataset.debugSwatchHover === 'true' || window.__DEBUG_SWATCH_HOVER__)) {
      console.warn('[ProductDefaultCard] Swatch hover state restored (resetVariant was not called). Trigger:', trigger || 'unknown');
    }
    return restored;
  }

  /**
   * Show nav arrows when current variant has more than one slide; hide when one or none
   */
  #updateNavArrowsVisibility() {
    const arrows = this.querySelector('.card-gallery-default__nav-arrows');
    if (!arrows) return;
    const hasMultiple = this.#getNavigableSlideCount() > 1;
    arrows.classList.toggle('card-gallery-default__nav-arrows--hidden', !hasMultiple);
  }

  /**
   * Slides that exist but are parked out of the tree.
   *
   * `snippets/perf-defer-script.liquid` swaps every non-active slide for a
   * `<template>` placeholder until the shopper first interacts. Counting those
   * placeholders lets the navigation describe the real gallery on the very
   * first paint, instead of collapsing to a single slide and reappearing later.
   * The check looks at what a template actually holds rather than at the
   * deferral script's marker attribute, so it stays correct if that changes.
   * @returns {number}
   */
  #getParkedSlideCount() {
    const wrapper = this.querySelector('.card-gallery-default__slides-wrapper');
    if (!wrapper) return 0;

    let count = 0;
    for (const child of wrapper.children) {
      if (child.tagName !== 'TEMPLATE') continue;
      const parked = /** @type {HTMLTemplateElement} */ (child).content.firstElementChild;
      if (parked?.classList.contains('card-gallery-default__slide')) count += 1;
    }
    return count;
  }

  /**
   * How many slides the navigation should describe: the selected variant's
   * slides, or the whole set when the id cannot be resolved against the DOM
   * (placeholder slides, or an id left over from a previous variant), plus any
   * slide still parked by the deferral script.
   * @returns {number}
   */
  #getNavigableSlideCount() {
    const variantId = this.#getSelectedVariantId();
    const variantSlides = variantId ? this.#getSlidesForVariant(variantId) : [];
    return (variantSlides.length || this.slides.length) + this.#getParkedSlideCount();
  }

  /**
   * Keep dots and the bottom progress bar in sync with the visible slide.
   *
   * `slideCount` is resolved from the DOM when the caller doesn't supply one so
   * that every call leaves a complete, consistent state. Treating it as absent
   * used to skip the `item.hidden` reset while still clearing the container's
   * `--hidden` class, which left the container visible with only its first item
   * unhidden - a single lonely dot on a multi-image card.
   *
   * @param {number} activeIndex
   * @param {number} [slideCount]
   */
  #syncGalleryNavItems(activeIndex, slideCount) {
    const resolvedCount = Number.isFinite(slideCount)
      ? /** @type {number} */ (slideCount)
      : this.#getNavigableSlideCount();
    const items = this.querySelectorAll('[data-gallery-nav-item]');
    const hideNav = resolvedCount <= 1;

    this.querySelectorAll('.card-gallery-default__nav-dots').forEach((container) => {
      container.classList.toggle('card-gallery-default__nav-dots--hidden', hideNav);
    });
    this.querySelectorAll('.card-gallery-default__nav-progress').forEach((container) => {
      container.classList.toggle('card-gallery-default__nav-progress--hidden', hideNav);
    });

    items.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      const index = parseInt(item.dataset.slideIndex || '0', 10);
      if (index >= resolvedCount) {
        item.hidden = true;
        item.classList.remove('card-gallery-default__dot--active', 'card-gallery-default__nav-progress-segment--active');
        item.setAttribute('aria-selected', 'false');
        return;
      }
      item.hidden = false;
      const isActive = index === activeIndex;
      if (item.classList.contains('card-gallery-default__nav-progress-segment')) {
        item.classList.toggle('card-gallery-default__nav-progress-segment--active', isActive);
      } else {
        item.classList.toggle('card-gallery-default__dot--active', isActive);
      }
      item.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  /**
   * Update navigation dots for selected variant's slides
   * @param {string | null | undefined} variantId - When it doesn't resolve to any slide, the full slide set is used
   * @param {number} [activeIndexOverride] - Slide index to highlight immediately (e.g. while a swipe animation is still running)
   */
  #updateDotsForVariant(variantId, activeIndexOverride) {
    const variantSlides = variantId ? this.#getSlidesForVariant(variantId) : [];
    const countedSlides = variantSlides.length ? variantSlides : this.slides;
    const currentSlide = countedSlides.find((slide) => !slide.hidden);
    const currentImageIndex = Number.isFinite(activeIndexOverride)
      ? /** @type {number} */ (activeIndexOverride)
      : (currentSlide ? parseInt(currentSlide.dataset.imageIndex || '0', 10) : 0);
    this.#syncGalleryNavItems(currentImageIndex, countedSlides.length + this.#getParkedSlideCount());
  }

  /**
   * Check if navigation was used (public method)
   */
  get navigationUsed() {
    return this.#navigationUsed;
  }

  /**
   * Check if swatch is selected (public method)
   */
  get swatchSelected() {
    return this.#swatchSelected;
  }

  /**
   * Reset to selected variant image (internal) - restore the slide we changed on hover
   */
  #resetVariant = () => {
    this.#restoreHoverPreviewState('resetVariant');
    this.#clearSwatchHoverState();
  };

  /**
   * Debounced reset variant (for hover leave) - called by declarative event system
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

/**
 * Swatches Variant Picker Component
 * Extends VariantPicker to add swatch-specific hover and selection behavior
 *
 * @extends {VariantPicker}
 */
class SwatchesDefaultVariantPickerComponent extends VariantPicker {
  /** @type {ProductDefaultCard | null} */
  parentProductCard = null;

  /** @type {string | null} */
  pendingVariantId = null;

  /** @type {Map<HTMLElement, EventListener>} - Store swatch click handlers for cleanup */
  #swatchClickHandlers = new Map();

  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-default-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));

    // Ensure initial selected state is correct
    this.#updateSelectedClassFromChecked();
    
    // Initialize swatch visibility immediately (synchronously) to prevent flash
    // Run before first paint to avoid showing all swatches then hiding them
    this.#initializeSwatchVisibility();

    // CRITICAL: Add explicit click handlers for iOS compatibility
    // iOS Safari has issues with labels containing visually-hidden inputs
    // The change event may not fire properly when tapping on labels
    this.#initializeSwatchClickHandlers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Clean up swatch click handlers
    this.#removeSwatchClickHandlers();
  }

  /**
   * Initialize touch/click handlers on swatch labels for iOS compatibility
   * iOS Safari has two issues:
   * 1. First tap triggers pointerenter (hover simulation), second tap triggers click
   * 2. Change events on hidden radio inputs may not fire properly
   * 
   * Solution: Use touchend event to bypass iOS hover simulation and trigger selection immediately
   */
  #initializeSwatchClickHandlers() {
    const swatchLabels = this.querySelectorAll('label.swatches-default__swatch');
    
    // Detect iOS device
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    swatchLabels.forEach((label) => {
      if (!(label instanceof HTMLElement)) return;
      
      // Skip if handler already attached
      if (this.#swatchClickHandlers.has(label)) return;
      
      /** @type {EventListener} */
      const touchEndHandler = (e) => {
        // Only handle single touch
        const touchEvent = /** @type {TouchEvent} */ (e);
        if (touchEvent.changedTouches && touchEvent.changedTouches.length !== 1) return;
        
        const input = label.querySelector('input[type="radio"]');
        if (!(input instanceof HTMLInputElement)) return;
        
        // If input is already checked (same swatch clicked again), do nothing
        if (input.checked && input.hasAttribute('data-current-checked')) return;
        
        // CRITICAL: Prevent default to stop iOS from triggering hover simulation
        // This prevents the double-tap issue
        e.preventDefault();
        e.stopPropagation();
        
        // Explicitly check the radio button
        input.checked = true;
        
        // Dispatch change event manually
        const changeEvent = new Event('change', { bubbles: true, cancelable: true });
        input.dispatchEvent(changeEvent);
      };
      
      /** @type {EventListener} */
      const clickHandler = (e) => {
        const input = label.querySelector('input[type="radio"]');
        if (!(input instanceof HTMLInputElement)) return;
        
        // If input is already checked (same swatch clicked again), do nothing
        if (input.checked && input.hasAttribute('data-current-checked')) return;
        
        // Explicitly check the radio button
        input.checked = true;
        
        // Dispatch change event manually
        const changeEvent = new Event('change', { bubbles: true, cancelable: true });
        input.dispatchEvent(changeEvent);
      };
      
      if (isIOS) {
        // On iOS: Use touchend to bypass hover simulation and double-tap issue
        // touchend fires immediately on first tap, before iOS's hover delay
        label.addEventListener('touchend', touchEndHandler, { passive: false });
        this.#swatchClickHandlers.set(label, touchEndHandler);
        
        // Add CSS to disable double-tap-to-zoom on this element
        label.style.touchAction = 'manipulation';
      } else {
        // On other devices: Use click event
        label.addEventListener('click', clickHandler);
        this.#swatchClickHandlers.set(label, clickHandler);
      }
    });
  }

  /**
   * Remove swatch click handlers (cleanup)
   */
  #removeSwatchClickHandlers() {
    // Detect iOS device for proper cleanup
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    this.#swatchClickHandlers.forEach((handler, element) => {
      if (isIOS) {
        element.removeEventListener('touchend', /** @type {EventListener} */ (handler));
      } else {
        element.removeEventListener('click', /** @type {EventListener} */ (handler));
      }
    });
    this.#swatchClickHandlers.clear();
  }

  /**
   * Override parent's updateVariantPicker to preserve max_visible_swatches attribute and reinitialize visibility
   * @param {Document} newHtml - The new HTML
   * @returns {{id: string, url: string} | undefined} Information about the new product if it has changed
   */
  updateVariantPicker(newHtml) {
    // Preserve max_visible_swatches before morphing (always preserve existing value)
    const maxVisibleSwatches = this.dataset.maxVisibleSwatches;
    const totalSwatchesBefore = this.dataset.totalSwatches;
    
    // Call parent's updateVariantPicker
    const newProduct = super.updateVariantPicker(newHtml);
    
    // Always restore max_visible_swatches after morphing (preserve from existing component)
    if (maxVisibleSwatches) {
      this.dataset.maxVisibleSwatches = maxVisibleSwatches;
    }
    // Server-rendered total swatch count: keep the morphed value when present, otherwise fall back
    // to the value we had before the morph (older markup / Unbxd cards may not send it).
    if (!this.dataset.totalSwatches && totalSwatchesBefore) {
      this.dataset.totalSwatches = totalSwatchesBefore;
    }
    
    // Reinitialize swatch visibility and click handlers after morphing
    // Use requestAnimationFrame to ensure DOM is fully updated after morphing
    requestAnimationFrame(() => {
      this.#initializeSwatchVisibility();
      
      // CRITICAL: Reinitialize iOS click handlers after morphing
      // Old labels may be replaced by new ones during morph
      this.#removeSwatchClickHandlers();
      this.#initializeSwatchClickHandlers();
    });
    
    return newProduct;
  }

  /**
   * Override parent's variantChanged to always use normal section
   * This prevents the parent from using the hardcoded SECTION_ID_MAP
   * @param {Event} event - The change event from radio input
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    const selectedOption = event.target;
    const variantId = selectedOption.dataset.variantId;
    const mediaId = selectedOption.dataset.optionMediaId;

    // Call parent's updateSelectedOption for variant picker logic
    this.updateSelectedOption(selectedOption);
    
    // Dispatch variant selected event (for other components listening)
    this.dispatchEvent(
      new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' })
    );

    // Update selected class immediately for visual feedback
    this.#updateSelectedClass(selectedOption);

    if (variantId) {
      // Store the variant ID for URL update after fetch
      this.pendingVariantId = variantId;

      // Build request URL - always use normal section
      const productUrl = this.dataset.productUrl?.split('?')[0];
      if (!productUrl) return;

      // Always use normal section (simple section has been removed)
      const sectionId = 'section-rendering-product-default-normal';

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variantId);
      url.searchParams.set('section_id', sectionId);
      
      // Fetch updated section (max_visible_swatches is preserved in data attribute)
      this.fetchUpdatedSection(url.href);
    }
  }

  /**
   * Update the selected class on swatches
   * @param {HTMLInputElement} selectedInput - The selected input element
   */
  #updateSelectedClass(selectedInput) {
    // Remove selected class from all swatches and update checked states
    this.querySelectorAll('.swatches-default__swatch').forEach((swatch) => {
      swatch.classList.remove('swatches-default__swatch--selected');
      const input = swatch.querySelector('.swatches-default__input');
      if (input) input.removeAttribute('data-current-checked');
    });

    // Add selected class to the clicked swatch's label
    const selectedLabel = selectedInput.closest('.swatches-default__swatch');
    if (selectedLabel) {
      selectedLabel.classList.add('swatches-default__swatch--selected');
    }
    selectedInput.setAttribute('data-current-checked', 'true');
  }

  /**
   * Updates the card URL when a variant is selected.
   * Also updates the selected class after morphing to ensure visual state is correct.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductDefaultCard) {
      const productCardLink = this.parentProductCard.refs.productCardLink;
      if (productCardLink) {
        const currentUrl = new URL(productCardLink.href);
        currentUrl.searchParams.set('variant', this.pendingVariantId);
        productCardLink.href = currentUrl.toString();
      }
      this.pendingVariantId = null;
    }

    // Update selected class after morphing to ensure visual state is correct
    this.#updateSelectedClassFromChecked();
  }

  /**
   * Update selected class based on data-current-checked attribute after morphing
   */
  #updateSelectedClassFromChecked() {
    // Remove selected class from all swatches
    this.querySelectorAll('.swatches-default__swatch').forEach((swatch) => {
      swatch.classList.remove('swatches-default__swatch--selected');
    });

    // Find the input with data-current-checked="true" and add selected class to its label
    const checkedInput = this.querySelector('.swatches-default__input[data-current-checked="true"]');
    const selectedLabel = checkedInput?.closest('.swatches-default__swatch');
    if (selectedLabel) {
      selectedLabel.classList.add('swatches-default__swatch--selected');
    }
  }

  /**
   * Initialize swatch visibility based on max_visible_swatches data attribute
   * Shows/hides swatches and overflow button dynamically
   */
  #initializeSwatchVisibility() {
    // Get swatch list first (early return if not found)
    const swatchList = this.querySelector('.swatches-default__list');
    if (!swatchList) {
      return;
    }
    
    // Get all swatch labels and overflow button (cache for performance)
    const allSwatches = Array.from(swatchList.querySelectorAll('label.swatches-default__swatch'));
    const overflowButton = swatchList.querySelector('button.swatches-default__swatch--overflow');
    
    const totalSwatches = allSwatches.length;
    
    // Early return if no swatches
    if (totalSwatches === 0) {
      return;
    }
    
    // Get max_visible_swatches from data attribute, default to 5
    const maxVisibleSwatches = parseInt(this.dataset.maxVisibleSwatches || '5', 10);

    // The server now renders only `max_visible_swatches` swatches and tells us the real total via
    // data-total-swatches, so the +X count must be computed from that total - not from the DOM.
    // Fallback to the legacy DOM-based maths when the attribute is missing (older markup / Unbxd).
    const serverTotal = parseInt(this.dataset.totalSwatches || '', 10);
    const visibleSwatches = Math.min(totalSwatches, maxVisibleSwatches);
    const overflowCount = Number.isFinite(serverTotal) && serverTotal > 0
      ? serverTotal - visibleSwatches
      : totalSwatches - maxVisibleSwatches;
    
    // Mark swatches beyond the limit as "collapsed" (hidden on every breakpoint). Normally the DOM
    // already holds at most `max_visible_swatches` swatches, so this only kicks in when a morph
    // response was rendered with a different limit than this card's block setting.
    for (let i = 0; i < totalSwatches; i++) {
      const swatch = allSwatches[i];
      if (swatch instanceof HTMLElement) {
        swatch.classList.toggle('swatches-default__swatch--collapsed', i >= maxVisibleSwatches);
        // Clean up the legacy attribute in case older markup still carries it
        swatch.removeAttribute('hidden');
      }
    }
    
    if (!(overflowButton instanceof HTMLElement)) {
      return;
    }
    
    // Show the overflow button only when there are swatches not rendered on the card.
    // The +X button is shown on every breakpoint (desktop and mobile/tablet).
    if (overflowCount > 0) {
      const overflowCountSpan = overflowButton.querySelector('.swatches-default__overflow-count');
      if (overflowCountSpan instanceof HTMLElement) {
        overflowCountSpan.textContent = `+${overflowCount}`;
        
        // Update aria-label using default aria-label from data attribute or fallback
        const defaultAriaLabel = overflowButton.dataset.defaultAriaLabel || 'View all options';
        overflowButton.setAttribute('aria-label', `${defaultAriaLabel} (+${overflowCount})`);
      }
      overflowButton.removeAttribute('hidden');
    } else {
      overflowButton.setAttribute('hidden', '');
    }
  }
}

// Register custom elements
if (!customElements.get('product-default-card')) {
  customElements.define('product-default-card', ProductDefaultCard);
}

if (!customElements.get('swatches-default-variant-picker-component')) {
  customElements.define('swatches-default-variant-picker-component', SwatchesDefaultVariantPickerComponent);
}
